import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Users, Send, Shield, MessageSquare, Radio, Check, CheckCheck, Loader2, Search, Phone, MoreVertical, Trash2, Download, X, FileText, ExternalLink, CornerUpRight, Calendar, Plus, Edit, Clock, LayoutList, LayoutGrid, Eye, Maximize2 } from "lucide-react";
import axios from "axios";
import socket from "../../socket";
import {
  fetchMembers,
  toggleUserPermissions,
  sendBroadcastMessage,
  createOrGetConversation,
  fetchMessages,
  resetChatUnreadCount,
  fetchConversations,
  setChatDrawerOpen
} from "../../Redux/Slice/ChatSlice";
import defaultProfile from "../../Image/Daynamic_Logo_Piture.png";
import DynamicTable from "../../Components/Table/DynamicTable";


const departments = ["All", "Web", "Delphi", "Support"];

const OwnerChatManagement = () => {
  const dispatch = useDispatch();

  const { members, onlineUsers, messages, activeConversation, messagesLoading, conversations } = useSelector(
    (state) => state.chat
  );
  const authData = useSelector((state) => state.auth.LoginData || {});
  const currentUserId = authData?.data?._id;

  const [lightboxImage, setLightboxImage] = useState(null);

  const getFileName = (url) => {
    if (!url) return "File";
    const parts = url.split("/");
    const lastPart = parts[parts.length - 1];
    const underscoreIndex = lastPart.indexOf("_");
    return underscoreIndex !== -1
      ? decodeURIComponent(lastPart.substring(underscoreIndex + 1))
      : decodeURIComponent(lastPart);
  };

  const getFileExtension = (url) => {
    if (!url) return "DOC";
    const parts = url.split(".");
    const ext = parts[parts.length - 1].toUpperCase();
    return ext.length <= 4 ? ext : "DOC";
  };

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes) || bytes <= 0) return "";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const parseMessageMarkdown = (content, isSelectedMember) => {
    if (!content || typeof content !== "string") return content;

    const parts = content.split("```");
    return parts.map((part, index) => {
      const isCodeBlock = index % 2 === 1;
      if (isCodeBlock) {
        let cleanPart = part;
        if (cleanPart.startsWith("text\n")) {
          cleanPart = cleanPart.substring(5);
        } else if (cleanPart.startsWith("\n")) {
          cleanPart = cleanPart.substring(1);
        }
        return (
          <pre
            key={index}
            style={{
              fontFamily: 'monospace, Courier New, Courier',
              fontSize: '10px',
              backgroundColor: isSelectedMember ? 'rgba(0, 0, 0, 0.15)' : '#f1f5f9',
              color: isSelectedMember ? '#ffffff' : '#0f172a',
              padding: '6px 8px',
              borderRadius: '6px',
              margin: '6px 0',
              overflowX: 'auto',
              whiteSpace: 'pre',
              border: isSelectedMember ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid #e2e8f0',
              lineHeight: '1.4'
            }}
          >
            {cleanPart}
          </pre>
        );
      } else {
        const lines = part.split("\n");
        return lines.map((line, lineIdx) => {
          const boldParts = line.split("*");
          const renderedLine = boldParts.map((bPart, bIdx) => {
            const isBold = bIdx % 2 === 1;
            if (isBold) {
              return <strong key={bIdx} style={{ fontWeight: '800' }}>{bPart}</strong>;
            }
            return <React.Fragment key={bIdx}>{bPart}</React.Fragment>;
          });

          return (
            <React.Fragment key={lineIdx}>
              {renderedLine}
              {lineIdx < lines.length - 1 && <br />}
            </React.Fragment>
          );
        });
      }
    });
  };

  const scrollToAuditMessage = (messageId) => {
    const element = document.getElementById(`audit-msg-${messageId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.classList.add("message-flash-highlight");
      setTimeout(() => {
        element.classList.remove("message-flash-highlight");
      }, 1200);
    }
  };

  const [activeTab, setActiveTab] = useState("permissions"); // 'permissions', 'broadcast', 'groups'
  const [memberSearch, setMemberSearch] = useState("");
  // Broadcast Compose States
  const [broadcastTarget, setBroadcastTarget] = useState("all"); // 'all', 'department', 'selected'
  const [selectedDept, setSelectedDept] = useState("Web");
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
  const [broadcastText, setBroadcastText] = useState("");
  const [isSendingBroadcast, setIsSendingBroadcast] = useState(false);

  // Group Creation States
  const [groupName, setGroupName] = useState("");
  const [groupNameTouched, setGroupNameTouched] = useState(false);
  const [groupImage, setGroupImage] = useState("");
  const [selectedGroupMemberIds, setSelectedGroupMemberIds] = useState([]);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  // Edit Group Members Modal States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingMemberIds, setEditingMemberIds] = useState([]);
  const [editSearchQuery, setEditSearchQuery] = useState("");
  const [isSavingMembers, setIsSavingMembers] = useState(false);
  const [editGroupName, setEditGroupName] = useState("");
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Chat Settings States
  const [chatFileSizeLimitInput, setChatFileSizeLimitInput] = useState(25);
  const [isSavingFileSizeLimit, setIsSavingFileSizeLimit] = useState(false);

  // Scheduler States
  const [scheduleTimes, setScheduleTimes] = useState([]);
  const [newScheduleTime, setNewScheduleTime] = useState("10:00");
  const [isSavingScheduleTime, setIsSavingScheduleTime] = useState(false);
  const [scheduleLogs, setScheduleLogs] = useState([]);
  const [scheduleLogsLoading, setScheduleLogsLoading] = useState(false);
  const [logsSubTab, setLogsSubTab] = useState("sent-messages"); // 'sent-messages', 'schedule-runs'
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [scheduleEnabled, setScheduleEnabled] = useState(true);

  // Checkout Scheduler States
  const [checkoutTimes, setCheckoutTimes] = useState([]);
  const [newCheckoutTime, setNewCheckoutTime] = useState("19:00");
  const [isSavingCheckoutTime, setIsSavingCheckoutTime] = useState(false);
  const [checkoutLogs, setCheckoutLogs] = useState([]);
  const [checkoutLogsLoading, setCheckoutLogsLoading] = useState(false);
  const [checkoutEnabled, setCheckoutEnabled] = useState(true);
  const [inTimeExcludedEmployees, setInTimeExcludedEmployees] = useState([]);
  const [outTimeExcludedEmployees, setOutTimeExcludedEmployees] = useState([]);
  const [inTimeSearch, setInTimeSearch] = useState("");
  const [outTimeSearch, setOutTimeSearch] = useState("");
  const [isInTimeModalOpen, setIsInTimeModalOpen] = useState(false);
  const [isOutTimeModalOpen, setIsOutTimeModalOpen] = useState(false);
  const [tempInTimeExcluded, setTempInTimeExcluded] = useState([]);
  const [tempOutTimeExcluded, setTempOutTimeExcluded] = useState([]);

  // Audit History States
  const [selectedAuditMember, setSelectedAuditMember] = useState(null);
  const [auditMemberSearch, setAuditMemberSearch] = useState("");
  const [auditConversations, setAuditConversations] = useState([]);
  const [auditConversationsLoading, setAuditConversationsLoading] = useState(false);
  const [selectedAuditConversation, setSelectedAuditConversation] = useState(null);
  const [auditMessages, setAuditMessages] = useState([]);
  const [auditMessagesLoading, setAuditMessagesLoading] = useState(false);
  const [auditConvCounts, setAuditConvCounts] = useState({});
  const [loadingConvCounts, setLoadingConvCounts] = useState(false);
  const [auditPin, setAuditPin] = useState("");
  const [isAuditUnlocked, setIsAuditUnlocked] = useState(false);
  const [pinError, setPinError] = useState("");
  const auditMessagesContainerRef = useRef(null);
  const auditMessagesEndRef = useRef(null);

  const scrollAuditToBottom = () => {
    const container = auditMessagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
    if (auditMessagesEndRef.current) {
      auditMessagesEndRef.current.scrollIntoView({ behavior: "auto", block: "end" });
    }
  };

  useEffect(() => {
    if (auditMessages.length > 0) {
      scrollAuditToBottom();
      requestAnimationFrame(scrollAuditToBottom);
      setTimeout(scrollAuditToBottom, 60);
      setTimeout(scrollAuditToBottom, 200);
      setTimeout(scrollAuditToBottom, 500);
    }
  }, [auditMessages, auditMessagesLoading]);
  const [testNotificationLoading, setTestNotificationLoading] = useState(""); // "" (idle), "in", "out"
  const [testNotificationDate, setTestNotificationDate] = useState(new Date().toISOString().split("T")[0]);
  const [isOnlineModalOpen, setIsOnlineModalOpen] = useState(false);
  // Holiday Management States
  const [holidays, setHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayName, setHolidayName] = useState("");
  const [holidayDescription, setHolidayDescription] = useState("");
  const [holidaySendTime, setHolidaySendTime] = useState("10:00");
  const [isSavingHoliday, setIsSavingHoliday] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [isEditHolidayModalOpen, setIsEditHolidayModalOpen] = useState(false);
  const [editHolidayDate, setEditHolidayDate] = useState("");
  const [editHolidayName, setEditHolidayName] = useState("");
  const [editHolidayDescription, setEditHolidayDescription] = useState("");
  const [editHolidaySendTime, setEditHolidaySendTime] = useState("10:00");

  const [holidayImageFile, setHolidayImageFile] = useState(null);
  const [holidayImagePreview, setHolidayImagePreview] = useState(null);
  const [editHolidayImage, setEditHolidayImage] = useState("");
  const [editHolidayImageFile, setEditHolidayImageFile] = useState(null);
  const [editHolidayImagePreview, setEditHolidayImagePreview] = useState(null);

  const handleHolidayImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHolidayImageFile(file);
      setHolidayImagePreview(URL.createObjectURL(file));
    }
  };

  const clearHolidayImage = () => {
    setHolidayImageFile(null);
    setHolidayImagePreview(null);
  };

  const handleEditHolidayImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditHolidayImageFile(file);
      setEditHolidayImagePreview(URL.createObjectURL(file));
    }
  };

  const clearEditHolidayImage = () => {
    setEditHolidayImage("");
    setEditHolidayImageFile(null);
    setEditHolidayImagePreview(null);
  };

  const [holidayViewMode, setHolidayViewMode] = useState("list"); // 'list' | 'grid'
  const [holidaySearchQuery, setHolidaySearchQuery] = useState("");

  const formatHolidayDate = (dateVal) => {
    if (!dateVal) return { weekday: "", day: "", month: "", year: "", formatted: "" };
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return { weekday: "", day: "", month: "", year: "", formatted: String(dateVal) };
      const weekday = d.toLocaleDateString("en-US", { weekday: "short" });
      const day = d.toLocaleDateString("en-US", { day: "2-digit" });
      const month = d.toLocaleDateString("en-US", { month: "short" });
      const year = d.getFullYear();
      return { weekday, day, month, year, formatted: `${weekday}, ${day} ${month} ${year}` };
    } catch (e) {
      return { weekday: "", day: "", month: "", year: "", formatted: String(dateVal) };
    }
  };

  const formatSendTime12h = (timeStr) => {
    if (!timeStr) return "10:00 AM";
    const parts = timeStr.split(":");
    if (parts.length < 2) return timeStr;
    const h = parseInt(parts[0], 10);
    const m = parts[1];
    if (isNaN(h)) return timeStr;
    const ampm = h >= 12 ? "PM" : "AM";
    const formattedH = h % 12 || 12;
    return `${formattedH}:${m} ${ampm}`;
  };

  const filteredHolidays = holidays.filter((h) => {
    if (!holidaySearchQuery.trim()) return true;
    const q = holidaySearchQuery.toLowerCase();
    const nameMatch = (h.name || "").toLowerCase().includes(q);
    const descMatch = (h.description || "").toLowerCase().includes(q);
    const dateStrMatch = (h.dateStr || "").toLowerCase().includes(q);
    const dateFormatted = formatHolidayDate(h.date).formatted.toLowerCase();
    return nameMatch || descMatch || dateStrMatch || dateFormatted.includes(q);
  });

  // Load members and conversations on mount
  useEffect(() => {
    dispatch(fetchMembers());
    if (currentUserId) {
      dispatch(fetchConversations(currentUserId));
    }
  }, [dispatch, currentUserId]);



  const fetchHolidays = async () => {
    setHolidaysLoading(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await axios.get(`${BaseUrl}/Chat/holidays`);
      if (res.data && res.data.status === "success") {
        setHolidays(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching holidays:", err);
    } finally {
      setHolidaysLoading(false);
    }
  };

  const handleAddHoliday = async (e) => {
    if (e) e.preventDefault();
    if (!holidayDate || !holidayName.trim()) {
      alert("Date and Holiday Name are required.");
      return;
    }
    setIsSavingHoliday(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

      let uploadedUrl = "";
      if (holidayImageFile) {
        const formData = new FormData();
        formData.append("file", holidayImageFile);
        const uploadRes = await axios.post(`${BaseUrl}/Chat/upload`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        uploadedUrl = uploadRes.data?.url || "";
      }

      const res = await axios.post(`${BaseUrl}/Chat/holidays`, {
        date: holidayDate,
        name: holidayName,
        description: holidayDescription,
        image: uploadedUrl,
        sendTime: holidaySendTime || "10:00"
      });
      if (res.data && res.data.status === "success") {
        alert("Holiday added successfully!");
        setHolidayDate("");
        setHolidayName("");
        setHolidayDescription("");
        setHolidaySendTime("10:00");
        clearHolidayImage();
        fetchHolidays();
      }
    } catch (err) {
      console.error("Error adding holiday:", err);
      alert(err.response?.data?.message || "Failed to add holiday.");
    } finally {
      setIsSavingHoliday(false);
    }
  };

  const handleOpenEditHolidayModal = (holiday) => {
    setEditingHoliday(holiday);
    setEditHolidayDate(holiday.dateStr || holiday.date.split("T")[0]);
    setEditHolidayName(holiday.name);
    setEditHolidayDescription(holiday.description || "");
    setEditHolidaySendTime(holiday.sendTime || "10:00");
    setEditHolidayImage(holiday.image || "");
    setEditHolidayImagePreview(holiday.image || null);
    setEditHolidayImageFile(null);
    setIsEditHolidayModalOpen(true);
  };

  const handleUpdateHoliday = async (e) => {
    if (e) e.preventDefault();
    if (!editHolidayName.trim()) {
      alert("Holiday Name is required.");
      return;
    }
    setIsSavingHoliday(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

      let uploadedUrl = editHolidayImage;
      if (editHolidayImageFile) {
        const formData = new FormData();
        formData.append("file", editHolidayImageFile);
        const uploadRes = await axios.post(`${BaseUrl}/Chat/upload`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        uploadedUrl = uploadRes.data?.url || "";
      }

      const res = await axios.put(`${BaseUrl}/Chat/holidays/${editingHoliday._id}`, {
        date: editHolidayDate,
        name: editHolidayName,
        description: editHolidayDescription,
        image: uploadedUrl,
        sendTime: editHolidaySendTime || "10:00"
      });
      if (res.data && res.data.status === "success") {
        alert("Holiday updated successfully!");
        setIsEditHolidayModalOpen(false);
        setEditingHoliday(null);
        clearEditHolidayImage();
        fetchHolidays();
      }
    } catch (err) {
      console.error("Error updating holiday:", err);
      alert(err.response?.data?.message || "Failed to update holiday.");
    } finally {
      setIsSavingHoliday(false);
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm("Are you sure you want to delete this holiday?")) return;
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await axios.delete(`${BaseUrl}/Chat/holidays/${id}`);
      if (res.data && res.data.status === "success") {
        alert("Holiday deleted successfully.");
        fetchHolidays();
      }
    } catch (err) {
      console.error("Error deleting holiday:", err);
      alert(err.response?.data?.message || "Failed to delete holiday.");
    }
  };

  const fetchAttendanceLogs = async (dateVal) => {
    setLogsLoading(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await axios.get(`${BaseUrl}/Chat/attendance-logs?date=${dateVal}`);
      if (res.data && res.data.status === "success") {
        setAttendanceLogs(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching attendance logs:", err);
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchScheduleSettings = async () => {
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await axios.get(`${BaseUrl}/Chat/schedule-settings`);
      if (res.data && res.data.status === "success") {
        setScheduleTimes(res.data.times || []);
        setScheduleEnabled(res.data.enabled !== false);
        setInTimeExcludedEmployees(res.data.excludedEmployees || []);
      }
    } catch (err) {
      console.error("Error fetching schedule settings:", err);
    }
  };

  const handleAddScheduleTime = async () => {
    if (!newScheduleTime) return;
    if (scheduleTimes.includes(newScheduleTime)) {
      alert("This execution time is already added.");
      return;
    }
    const updatedTimes = [...scheduleTimes, newScheduleTime].sort();
    setScheduleTimes(updatedTimes);

    // Auto-save silently to backend
    setIsSavingScheduleTime(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      await axios.post(`${BaseUrl}/Chat/schedule-settings`, { times: updatedTimes });
      fetchScheduleLogs();
    } catch (err) {
      console.error("Error auto-saving schedule settings:", err);
    } finally {
      setIsSavingScheduleTime(false);
    }
  };

  const handleDeleteScheduleTime = async (timeToDelete) => {
    const updatedTimes = scheduleTimes.filter(t => t !== timeToDelete);
    setScheduleTimes(updatedTimes);

    // Auto-save silently to backend
    setIsSavingScheduleTime(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      await axios.post(`${BaseUrl}/Chat/schedule-settings`, { times: updatedTimes });
      fetchScheduleLogs();
    } catch (err) {
      console.error("Error auto-saving schedule settings:", err);
    } finally {
      setIsSavingScheduleTime(false);
    }
  };

  const getTimeStatus = (timeVal) => {
    const todayStr = new Date().toDateString();
    const todayLog = scheduleLogs.find(
      (log) =>
        log.scheduledTime === timeVal &&
        new Date(log.executionTime).toDateString() === todayStr
    );
    if (!todayLog) return "pending";
    return todayLog.status;
  };

  const handleToggleScheduleEnabled = async () => {
    const nextVal = !scheduleEnabled;
    const confirmMsg = nextVal
      ? "Are you sure you want to ENABLE the daily In-Time scheduler?"
      : "Are you sure you want to DISABLE the daily In-Time scheduler?";
    if (!window.confirm(confirmMsg)) return;

    setScheduleEnabled(nextVal);

    setIsSavingScheduleTime(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      await axios.post(`${BaseUrl}/Chat/schedule-settings`, { enabled: nextVal });
    } catch (err) {
      console.error("Error auto-saving schedule toggle setting:", err);
    } finally {
      setIsSavingScheduleTime(false);
    }
  };

  const saveScheduleSettings = async () => {
    setIsSavingScheduleTime(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await axios.post(`${BaseUrl}/Chat/schedule-settings`, {
        times: scheduleTimes,
        excludedEmployees: inTimeExcludedEmployees
      });
      if (res.data && res.data.status === "success") {
        alert("Scheduler configuration saved successfully!");
        fetchScheduleLogs();
      }
    } catch (err) {
      console.error("Error saving schedule settings:", err);
      alert(err.response?.data?.message || "Failed to save schedule settings.");
    } finally {
      setIsSavingScheduleTime(false);
    }
  };

  const fetchScheduleLogs = async () => {
    setScheduleLogsLoading(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await axios.get(`${BaseUrl}/Chat/schedule-logs`);
      if (res.data && res.data.status === "success") {
        setScheduleLogs(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching schedule logs:", err);
    } finally {
      setScheduleLogsLoading(false);
    }
  };

  const fetchCheckoutScheduleSettings = async () => {
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await axios.get(`${BaseUrl}/Chat/checkout-schedule-settings`);
      if (res.data && res.data.status === "success") {
        setCheckoutTimes(res.data.times || []);
        setCheckoutEnabled(res.data.enabled !== false);
        setOutTimeExcludedEmployees(res.data.excludedEmployees || []);
      }
    } catch (err) {
      console.error("Error fetching checkout schedule settings:", err);
    }
  };

  const saveCheckoutScheduleSettings = async () => {
    setIsSavingCheckoutTime(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await axios.post(`${BaseUrl}/Chat/checkout-schedule-settings`, {
        times: checkoutTimes,
        excludedEmployees: outTimeExcludedEmployees
      });
      if (res.data && res.data.status === "success") {
        alert("Checkout scheduler configuration saved successfully!");
        fetchCheckoutScheduleLogs();
      }
    } catch (err) {
      console.error("Error saving checkout schedule settings:", err);
      alert(err.response?.data?.message || "Failed to save checkout schedule settings.");
    } finally {
      setIsSavingCheckoutTime(false);
    }
  };

  const handleToggleCheckoutEnabled = async () => {
    const nextVal = !checkoutEnabled;
    const confirmMsg = nextVal
      ? "Are you sure you want to ENABLE the daily Out-Time scheduler?"
      : "Are you sure you want to DISABLE the daily Out-Time scheduler?";
    if (!window.confirm(confirmMsg)) return;

    setCheckoutEnabled(nextVal);

    setIsSavingCheckoutTime(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      await axios.post(`${BaseUrl}/Chat/checkout-schedule-settings`, { enabled: nextVal });
    } catch (err) {
      console.error("Error auto-saving checkout schedule toggle setting:", err);
    } finally {
      setIsSavingCheckoutTime(false);
    }
  };

  const handleAddCheckoutTime = async () => {
    if (!newCheckoutTime) return;
    if (checkoutTimes.includes(newCheckoutTime)) {
      alert("This time is already scheduled!");
      return;
    }
    const updatedTimes = [...checkoutTimes, newCheckoutTime].sort();
    setCheckoutTimes(updatedTimes);

    // Auto-save
    setIsSavingCheckoutTime(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      await axios.post(`${BaseUrl}/Chat/checkout-schedule-settings`, { times: updatedTimes });
    } catch (err) {
      console.error("Error auto-saving checkout times:", err);
    } finally {
      setIsSavingCheckoutTime(false);
    }
  };

  const handleDeleteCheckoutTime = async (timeVal) => {
    const updatedTimes = checkoutTimes.filter(t => t !== timeVal);
    setCheckoutTimes(updatedTimes);

    // Auto-save
    setIsSavingCheckoutTime(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      await axios.post(`${BaseUrl}/Chat/checkout-schedule-settings`, { times: updatedTimes });
    } catch (err) {
      console.error("Error auto-deleting checkout time:", err);
    } finally {
      setIsSavingCheckoutTime(false);
    }
  };

  const getCheckoutTimeStatus = (timeStr) => {
    const todayStr = new Date().toDateString();
    const todayLog = checkoutLogs.find(
      (log) =>
        log.scheduledTime === timeStr &&
        new Date(log.executionTime).toDateString() === todayStr
    );
    if (!todayLog) return "pending";
    return todayLog.status;
  };

  const fetchCheckoutScheduleLogs = async () => {
    setCheckoutLogsLoading(true);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await axios.get(`${BaseUrl}/Chat/checkout-schedule-logs`);
      if (res.data && res.data.status === "success") {
        setCheckoutLogs(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching checkout schedule logs:", err);
    } finally {
      setCheckoutLogsLoading(false);
    }
  };

  const fetchFileSizeLimit = async () => {
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await axios.get(`${BaseUrl}/Chat/file-size-limit`);
      if (res.data && res.data.status === "success") {
        setChatFileSizeLimitInput(res.data.limit);
      }
    } catch (err) {
      console.error("Error fetching file size limit:", err);
    }
  };

  const saveChatFileSizeLimit = async () => {
    try {
      setIsSavingFileSizeLimit(true);
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await axios.post(`${BaseUrl}/Chat/file-size-limit`, { limit: Number(chatFileSizeLimitInput) });
      if (res.data && res.data.status === "success") {
        alert(`Chat file size limit updated successfully to ${res.data.limit}MB!`);
      }
    } catch (err) {
      console.error("Error saving file size limit:", err);
      alert(err.response?.data?.message || "Failed to save file size limit.");
    } finally {
      setIsSavingFileSizeLimit(false);
    }
  };

  useEffect(() => {
    if (activeTab === "test-notifications") {
      fetchAttendanceLogs(testNotificationDate);
      fetchScheduleSettings();
      fetchScheduleLogs();
      fetchCheckoutScheduleSettings();
      fetchCheckoutScheduleLogs();
      fetchFileSizeLimit();
    } else if (activeTab === "holidays") {
      fetchHolidays();
    }
  }, [activeTab, testNotificationDate]);

  useEffect(() => {
    if (activeTab !== "test-notifications" || scheduleTimes.length === 0) return;

    const timeouts = [];

    // Setup a real-time scheduler refresh for each pending schedule time
    scheduleTimes.forEach((timeStr) => {
      // Deduplicate: check if already fired today according to database logs
      const todayStr = new Date().toDateString();
      const alreadyFired = scheduleLogs.some(
        (log) =>
          log.scheduledTime === timeStr &&
          new Date(log.executionTime).toDateString() === todayStr
      );

      if (alreadyFired) return;

      try {
        const [hour, minute] = timeStr.split(":").map(Number);

        // Format current time components in Asia/Kolkata timezone
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: "Asia/Kolkata",
          year: "numeric", month: "numeric", day: "numeric",
          hour: "numeric", minute: "numeric", second: "numeric",
          hour12: false
        });

        const parts = formatter.formatToParts(new Date());
        const partVal = (type) => Number(parts.find(p => p.type === type).value);

        const cYear = partVal("year");
        const cMonth = partVal("month") - 1;
        const cDay = partVal("day");
        const cHour = partVal("hour") === 24 ? 0 : partVal("hour");
        const cMin = partVal("minute");
        const cSec = partVal("second");

        const nowKolkataMs = new Date(cYear, cMonth, cDay, cHour, cMin, cSec).getTime();
        // Fire 2 seconds after the minute to allow server to finish write execution
        const targetKolkataMs = new Date(cYear, cMonth, cDay, hour, minute, 2).getTime();

        const msToTarget = targetKolkataMs - nowKolkataMs;

        if (msToTarget > 0) {
          const timerId = setTimeout(() => {
            fetchScheduleLogs();
            fetchAttendanceLogs(testNotificationDate);
          }, msToTarget);
          timeouts.push(timerId);
        }
      } catch (err) {
        console.error("Error setting real-time schedule timer:", err);
      }
    });

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [activeTab, scheduleTimes, scheduleLogs, testNotificationDate]);

  useEffect(() => {
    if (activeTab !== "test-notifications" || checkoutTimes.length === 0) return;

    const timeouts = [];

    checkoutTimes.forEach((timeStr) => {
      const todayStr = new Date().toDateString();
      const alreadyFired = checkoutLogs.some(
        (log) =>
          log.scheduledTime === timeStr &&
          new Date(log.executionTime).toDateString() === todayStr
      );

      if (alreadyFired) return;

      try {
        const [hour, minute] = timeStr.split(":").map(Number);

        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: "Asia/Kolkata",
          year: "numeric", month: "numeric", day: "numeric",
          hour: "numeric", minute: "numeric", second: "numeric",
          hour12: false
        });

        const parts = formatter.formatToParts(new Date());
        const partVal = (type) => Number(parts.find(p => p.type === type).value);

        const cYear = partVal("year");
        const cMonth = partVal("month") - 1;
        const cDay = partVal("day");
        const cHour = partVal("hour") === 24 ? 0 : partVal("hour");
        const cMin = partVal("minute");
        const cSec = partVal("second");

        const nowKolkataMs = new Date(cYear, cMonth, cDay, cHour, cMin, cSec).getTime();
        const targetKolkataMs = new Date(cYear, cMonth, cDay, hour, minute, 2).getTime();

        const msToTarget = targetKolkataMs - nowKolkataMs;

        if (msToTarget > 0) {
          const timerId = setTimeout(() => {
            fetchCheckoutScheduleLogs();
            fetchAttendanceLogs(testNotificationDate);
          }, msToTarget);
          timeouts.push(timerId);
        }
      } catch (err) {
        console.error("Error setting real-time checkout schedule timer:", err);
      }
    });

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [activeTab, checkoutTimes, checkoutLogs, testNotificationDate]);

  const handlePermissionToggle = (empId, field, currentVal) => {
    const payload = {
      employeeId: empId,
      chatEnabled: field === "chatEnabled" ? !currentVal : undefined,
      sendEnabled: field === "sendEnabled" ? !currentVal : undefined,
      attendanceBotEnabled: field === "attendanceBotEnabled" ? !currentVal : undefined
    };

    // Find the current values for the user
    const member = members.find((m) => m._id === empId);
    if (member) {
      if (payload.chatEnabled === undefined) payload.chatEnabled = member.chatEnabled;
      if (payload.sendEnabled === undefined) payload.sendEnabled = member.sendEnabled;
      if (payload.attendanceBotEnabled === undefined) payload.attendanceBotEnabled = member.attendanceBotEnabled;
    }

    dispatch(toggleUserPermissions(payload)).then(() => {
      // Re-fetch members to verify
      dispatch(fetchMembers());
    });
  };

  // Open direct chat with member inside the main chat drawer
  const handleOpenMemberChat = (member) => {
    dispatch(
      createOrGetConversation({
        participants: [currentUserId, member._id],
        isGroup: false,
        userId: currentUserId
      })
    ).then(() => {
      dispatch(setChatDrawerOpen(true));
    });
  };


  const handleTestNotificationAll = async (type) => {
    setTestNotificationLoading(type);
    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await axios.post(`${BaseUrl}/Chat/test-attendance-notification`, { date: testNotificationDate, type });
      alert(res.data.message || `Test ${type === "in" ? "check-in" : "check-out"} notifications dispatched successfully!`);
      fetchAttendanceLogs(testNotificationDate);
    } catch (error) {
      console.error("Test notification error:", error);
      alert(error.response?.data?.message || "Failed to send test notification.");
    } finally {
      setTestNotificationLoading("");
    }
  };

  const handleDateChange = (e) => {
    const selected = e.target.value;
    if (!selected) return;

    const [year, month, day] = selected.split("-").map(Number);
    const localDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Prevent future dates
    if (localDate > today) {
      alert("Future dates cannot be selected!");
      return;
    }

    // Prevent Sundays (0 is Sunday in JS getDay())
    if (localDate.getDay() === 0) {
      alert("Sundays cannot be selected!");
      return;
    }

    setTestNotificationDate(selected);
  };

  // Execute broadcast composition
  const handleSendBroadcast = (e) => {
    e.preventDefault();
    if (!broadcastText.trim()) return;

    // Validate manual selection target
    if (broadcastTarget === "selected" && selectedRecipientIds.length === 0) {
      alert("Please select at least one recipient member.");
      return;
    }

    setIsSendingBroadcast(true);
    dispatch(
      sendBroadcastMessage({
        targetType: broadcastTarget,
        targetIds: broadcastTarget === "selected" ? selectedRecipientIds : [],
        departmentName: broadcastTarget === "department" ? selectedDept : "",
        message: broadcastText.trim(),
        userId: currentUserId
      })
    )
      .unwrap()
      .then(() => {
        alert("Broadcast announcement sent successfully!");
        setBroadcastText("");
        setSelectedRecipientIds([]);
        setIsSendingBroadcast(false);
      })
      .catch((err) => {
        alert("Failed to send broadcast: " + err);
        setIsSendingBroadcast(false);
      });
  };

  // Create group chat room
  const handleCreateGroup = (e) => {
    e.preventDefault();
    if (!groupName.trim() || selectedGroupMemberIds.length < 2) {
      alert("Please provide a group name and select at least 2 members.");
      return;
    }

    setIsCreatingGroup(true);
    dispatch(
      createOrGetConversation({
        participants: [currentUserId, ...selectedGroupMemberIds],
        isGroup: true,
        name: groupName.trim(),
        groupImage: groupImage.trim() || "",
        userId: currentUserId
      })
    )
      .unwrap()
      .then(() => {
        alert("Group room created successfully!");
        setGroupName("");
        setGroupNameTouched(false);
        setGroupImage("");
        setSelectedGroupMemberIds([]);
        setIsCreatingGroup(false);
        if (currentUserId) {
          dispatch(fetchConversations(currentUserId));
        }
        setActiveTab("permissions");
      })
      .catch((err) => {
        alert("Failed to create group: " + err);
        setIsCreatingGroup(false);
      });
  };

  // Format last seen timestamp nicely
  const formatLastSeen = (dateStr) => {
    if (!dateStr) return "offline";
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    if (date.toDateString() === today.toDateString()) {
      return `last seen today at ${timeStr}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `last seen yesterday at ${timeStr}`;
    } else {
      const dateStrFormatted = date.toLocaleDateString([], { month: "short", day: "numeric" });
      return `last seen ${dateStrFormatted} at ${timeStr}`;
    }
  };

  const handleDownloadImage = (url) => {
    const downloadUrl = `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/Chat/download-file?url=${encodeURIComponent(url)}`;
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = getFileName(url);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSelectAuditMember = async (member) => {
    setSelectedAuditMember(member);
    setSelectedAuditConversation(null);
    setAuditMessages([]);
    setAuditConversations([]);
    setAuditConversationsLoading(true);

    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const response = await axios.get(`${BaseUrl}/Chat/conversations?userId=${member._id}`);
      if (response.data && response.data.status === "success") {
        setAuditConversations(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching audit conversations:", error);
      alert("Failed to fetch conversations for the selected member.");
    } finally {
      setAuditConversationsLoading(false);
    }
  };

  const handleSelectAuditConversation = async (conversation) => {
    setSelectedAuditConversation(conversation);
    setAuditMessagesLoading(true);
    setAuditMessages([]);

    try {
      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const response = await axios.get(`${BaseUrl}/Chat/messages/${conversation._id}`);
      if (response.data && response.data.status === "success") {
        setAuditMessages(response.data.data.messages || []);
      }
    } catch (error) {
      console.error("Error fetching audit messages:", error);
      alert("Failed to fetch message history.");
    } finally {
      setAuditMessagesLoading(false);
    }
  };

  // Load conversation counts for all members for audit history
  useEffect(() => {
    if (activeTab === "history" && members && members.length > 0) {
      const fetchAllCounts = async () => {
        setLoadingConvCounts(true);
        const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
        const counts = {};
        try {
          await Promise.all(
            members.map(async (m) => {
              try {
                const response = await axios.get(`${BaseUrl}/Chat/conversations?userId=${m._id}`);
                if (response.data && response.data.status === "success") {
                  const activeConvs = (response.data.data || []).filter(c => c.lastMessage);
                  const uniqueUsers = new Set();
                  activeConvs.forEach(conv => {
                    (conv.participants || []).forEach(p => {
                      const pId = p._id || p;
                      if (pId !== m._id) {
                        uniqueUsers.add(pId);
                      }
                    });
                  });
                  counts[m._id] = uniqueUsers.size;
                } else {
                  counts[m._id] = 0;
                }
              } catch (err) {
                console.error(`Error loading counts for ${m.Name}:`, err);
                counts[m._id] = 0;
              }
            })
          );
          setAuditConvCounts(counts);
        } catch (error) {
          console.error("Error fetching all conversation counts:", error);
        } finally {
          setLoadingConvCounts(false);
        }
      };
      fetchAllCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, members]);

  const getAuditConversationDetails = (conversation, auditMemberId) => {
    if (conversation.isGroup) {
      return {
        name: conversation.name || "Group Chat",
        image: conversation.groupImage || defaultProfile,
        type: "Group"
      };
    }

    // Direct Chat: find the other participant
    const otherParticipant = conversation.participants.find(
      (p) => (p._id || p) !== auditMemberId
    );

    return {
      name: otherParticipant ? otherParticipant.Name : "Unknown Member",
      image: otherParticipant?.ProfilePhoto || defaultProfile,
      type: "Direct"
    };
  };


  // Delete group conversation
  const handleDeleteGroup = async (conversationId) => {
    if (window.confirm("Are you sure you want to delete this group channel? All messages will be permanently lost.")) {
      try {
        const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
        await axios.delete(`${BaseUrl}/Chat/conversations/group/${conversationId}`);
        alert("Group deleted successfully!");
        if (currentUserId) {
          dispatch(fetchConversations(currentUserId));
        }
      } catch (error) {
        console.error("Delete group error:", error);
        alert(error.response?.data?.message || "Failed to delete group.");
      }
    }
  };

  const handleOpenEditModal = (group) => {
    setEditingGroup(group);
    setEditGroupName(group.name || "");
    // Extract participant IDs, resolving objects to string IDs
    const memberIds = (group.participants || []).map((p) => p._id || p);
    setEditingMemberIds(memberIds);
    setEditSearchQuery("");
    setIsEditModalOpen(true);
  };

  const handleEditCheckbox = (id) => {
    setEditingMemberIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleUpdateMembers = async (e) => {
    if (e) e.preventDefault();
    if (!editingGroup) return;

    if (editingGroup.isDefaultSender) {
      if (!editGroupName.trim()) {
        alert("Please provide a name for the channel.");
        return;
      }
      setIsSavingMembers(true);
      try {
        const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
        await axios.post(`${BaseUrl}/Chat/conversations/group/manage`, {
          conversationId: editingGroup._id,
          action: "update",
          groupName: editGroupName.trim()
        });
        alert("Channel name updated successfully!");
        setIsEditModalOpen(false);
        dispatch(fetchMembers());
        if (currentUserId) {
          dispatch(fetchConversations(currentUserId));
        }
      } catch (error) {
        console.error("Update channel name error:", error);
        alert(error.response?.data?.message || "Failed to update channel name.");
      } finally {
        setIsSavingMembers(false);
      }
      return;
    }

    if (!editGroupName.trim() || editingMemberIds.length < 2) {
      alert("Please provide a group name and select at least 2 members.");
      return;
    }

    setIsSavingMembers(true);
    try {
      // Ensure owner is included in the updated list
      const updatedIds = editingMemberIds.includes(currentUserId)
        ? editingMemberIds
        : [currentUserId, ...editingMemberIds];

      const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      await axios.post(`${BaseUrl}/Chat/conversations/group/manage`, {
        conversationId: editingGroup._id,
        action: "update_members",
        memberIds: updatedIds,
        groupName: editGroupName.trim()
      });

      alert("Group updated successfully!");
      setIsEditModalOpen(false);
      if (currentUserId) {
        dispatch(fetchConversations(currentUserId));
      }
    } catch (error) {
      console.error("Update group error:", error);
      alert(error.response?.data?.message || "Failed to update group.");
    } finally {
      setIsSavingMembers(false);
    }
  };

  // Recipients checkbox selection helpers
  const handleRecipientCheckbox = (id) => {
    setSelectedRecipientIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleGroupCheckbox = (id) => {
    setSelectedGroupMemberIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Check read checkmarks inside embedded console
  const renderMessageReceipts = (msg) => {
    const senderId = msg.sender?._id || msg.sender;
    if (senderId?.toString() !== currentUserId?.toString()) return null;

    const recipientIds = (activeConversation?.participants || [])
      .map((p) => p._id || p)
      .filter((id) => id && id.toString() !== currentUserId?.toString());

    const seenBy = msg.seenBy || [];
    const deliveredTo = msg.deliveredTo || [];

    const isRead = recipientIds.length > 0 && recipientIds.every((id) => seenBy.some((s) => (s.userId?._id || s.userId)?.toString() === id.toString()));
    const isDelivered = recipientIds.length > 0 && recipientIds.every((id) => deliveredTo.some((d) => (d.userId?._id || d.userId)?.toString() === id.toString()));

    if (isRead) {
      return (
        <span className="inline-flex items-center ml-1 text-[#38bdf8] shrink-0" title="Read">
          <svg viewBox="0 0 16 11" width="14" height="9.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
            <path d="M11.07 1.25L4.57 8.75L1.57 5.75" />
            <path d="M14.57 1.25L8.07 8.75L6.57 7.25" />
          </svg>
        </span>
      );
    } else if (isDelivered) {
      return (
        <span className="inline-flex items-center ml-1 text-white/70 shrink-0" title="Delivered">
          <svg viewBox="0 0 16 11" width="14" height="9.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
            <path d="M11.07 1.25L4.57 8.75L1.57 5.75" />
            <path d="M14.57 1.25L8.07 8.75L6.57 7.25" />
          </svg>
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center ml-1 text-white/55 shrink-0" title="Sent">
          <svg viewBox="0 0 16 11" width="12" height="9.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
            <path d="M11.07 1.25L4.57 8.75L1.57 5.75" />
          </svg>
        </span>
      );
    }
  };

  // Filters
  const filteredMembers = members.filter((m) =>
    m.Name.toLowerCase().includes(memberSearch.toLowerCase())
  );

  const columns = [
    {
      field: "Name",
      headerName: "Member",
      flex: 1.5,
      renderCell: (params) => {
        const member = params.row;
        const isOnline = onlineUsers[member._id];
        return (
          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              <img
                src={member.ProfilePhoto || defaultProfile}
                alt="Avatar"
                className="w-10 h-10 rounded-xl border object-cover shadow-xs"
                onError={(e) => {
                  e.target.src = defaultProfile;
                }}
              />
              {isOnline && (
                <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full animate-pulse"></span>
              )}
            </div>
            <div className="min-w-0">
              <span className="text-sm font-bold text-slate-800 block truncate">{member.Name}</span>
              {!isOnline && member.Email !== "management@dynamic.com" && (
                <span className="text-[11px] text-slate-550 block mt-0.5">
                  {formatLastSeen(member.lastSeen)}
                </span>
              )}
            </div>
          </div>
        );
      }
    },
    {
      field: "Role",
      headerName: "Role",
      flex: 1.2,
      renderCell: (params) => (
        <span className="text-xs font-bold text-slate-550">
          {params.value}
        </span>
      )
    },
    {
      field: "chatEnabled",
      headerName: "Chat Access",
      flex: 1,
      renderCell: (params) => {
        const member = params.row;
        return (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={member.chatEnabled !== false}
              onChange={() =>
                handlePermissionToggle(member._id, "chatEnabled", member.chatEnabled !== false)
              }
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-350 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#133a69]"></div>
          </label>
        );
      }
    },
    {
      field: "sendEnabled",
      headerName: "Send Privilege",
      flex: 1,
      renderCell: (params) => {
        const member = params.row;
        return (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={member.sendEnabled !== false}
              onChange={() =>
                handlePermissionToggle(member._id, "sendEnabled", member.sendEnabled !== false)
              }
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-350 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#133a69]"></div>
          </label>
        );
      }
    },
    {
      field: "attendanceBotEnabled",
      headerName: "Bot Access",
      flex: 1,
      renderCell: (params) => {
        const member = params.row;
        return (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={member.attendanceBotEnabled !== false}
              onChange={() =>
                handlePermissionToggle(member._id, "attendanceBotEnabled", member.attendanceBotEnabled !== false)
              }
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-350 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#133a69]"></div>
          </label>
        );
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        const member = params.row;
        return (
          <button
            onClick={() => handleOpenMemberChat(member)}
            className="px-3.5 py-2 bg-blue-50 hover:bg-[#133a69] text-[#133a69] hover:text-white rounded-xl text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1.5 shadow-xs border border-transparent hover:border-[#133a69]"
          >
            <MessageSquare size={13} /> Chat
          </button>
        );
      }
    }
  ];

  const emptyMemberComponent = (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="w-16 h-16 bg-[#F0F7FF] rounded-full flex items-center justify-center mb-4 shadow-sm border border-[#E8F2FF]">
        <Users className="w-8 h-8 text-[#133a69]" />
      </div>
      <h3 className="text-base font-bold text-[#133a69] mb-1 tracking-wide">No members found</h3>
      <p className="text-gray-500 text-sm text-center max-w-[280px] leading-relaxed">
        Try searching for a different employee name.
      </p>
    </div>
  );

  const rows = filteredMembers.map((member) => ({
    id: member._id,
    ...member
  }));

  const totalOnlineCount = Object.entries(onlineUsers).filter(([userId, isOnline]) => isOnline && userId !== currentUserId).length;


  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 p-4 md:p-6 font-sans">
      {/* Page Header Title */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-[#133a69] to-[#1e40af] text-white rounded-2xl shadow-md shadow-blue-500/10">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-800 tracking-tight">
                Owner Chat Management
              </h1>
              <p className="text-xs md:text-sm text-slate-500 font-medium mt-0.5">
                Administer system chat privileges, broadcasts, group structures, and audit coworker communications.
              </p>
            </div>
          </div>
        </div>

        {/* Dynamic Statistics Cards */}
        <div className="flex flex-wrap gap-4 w-full lg:w-auto">
          <div className="bg-white px-5 py-3.5 rounded-2xl border border-slate-200/80 shadow-[0_8px_30px_rgb(0,0,0,0.02)] flex items-center gap-4 min-w-[170px] flex-1 lg:flex-none hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-100 transition-all duration-300">
            <div className="p-3 bg-blue-50/80 text-[#133a69] rounded-xl shrink-0">
              <Users size={18} />
            </div>
            <div className="flex flex-col items-start min-w-0">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block truncate">Total Members</span>
              <span className="text-2xl font-black text-slate-800 leading-none mt-1 block">{members.length}</span>
            </div>
          </div>

          <div
            onClick={() => setIsOnlineModalOpen(true)}
            className="bg-white px-5 py-3.5 rounded-2xl border border-slate-200/80 shadow-[0_8px_30px_rgb(0,0,0,0.02)] flex items-center gap-4 min-w-[170px] flex-1 lg:flex-none hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-100 transition-all duration-300 cursor-pointer select-none"
          >
            <div className="p-3 bg-emerald-50/80 text-emerald-600 rounded-xl relative flex items-center justify-center shrink-0">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping absolute"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 relative"></span>
            </div>
            <div className="flex flex-col items-start min-w-0">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block truncate">Online Now</span>
              <span className="text-2xl font-black text-emerald-600 leading-none mt-1 block">{totalOnlineCount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Container Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Side: Control Panel (Toggles, Broadcast, Groups, Audit) */}
        <div className="lg:col-span-12 bg-white rounded-2xl border border-slate-200/80 shadow-md shadow-slate-100/50 overflow-hidden flex flex-col min-h-[620px]">
          {/* Navigation Bar Header */}
          <div className="flex gap-2 border-b border-slate-150 bg-slate-50/50 p-3 overflow-x-auto shrink-0">
            <button
              onClick={() => setActiveTab("permissions")}
              className={`py-2 px-4 text-xs font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer border-none ${activeTab === "permissions"
                ? "bg-[#133a69] text-white shadow-md shadow-blue-900/10 font-semibold"
                : "bg-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-200/50"
                }`}
            >
              <Shield size={14} /> Member Permissions
            </button>
            <button
              onClick={() => setActiveTab("broadcast")}
              className={`py-2 px-4 text-xs font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer border-none ${activeTab === "broadcast"
                ? "bg-[#133a69] text-white shadow-md shadow-blue-900/10 font-semibold"
                : "bg-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-200/50"
                }`}
            >
              <Radio size={14} /> Broadcast Messaging
            </button>
            <button
              onClick={() => setActiveTab("groups")}
              className={`py-2 px-4 text-xs font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer border-none ${activeTab === "groups"
                ? "bg-[#133a69] text-white shadow-md shadow-blue-900/10 font-semibold"
                : "bg-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-200/50"
                }`}
            >
              <Users size={14} /> Group Creator
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`py-2 px-4 text-xs font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer border-none ${activeTab === "history"
                ? "bg-[#133a69] text-white shadow-md shadow-blue-900/10 font-semibold"
                : "bg-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-200/50"
                }`}
            >
              <MessageSquare size={14} /> Member Chat Audit
            </button>
            <button
              onClick={() => setActiveTab("test-notifications")}
              className={`py-2 px-4 text-xs font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer border-none ${activeTab === "test-notifications"
                ? "bg-[#133a69] text-white shadow-md shadow-blue-900/10 font-semibold"
                : "bg-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-200/50"
                }`}
            >
              <CheckCheck size={14} /> Attendance Notifications
            </button>
            <button
              onClick={() => setActiveTab("holidays")}
              className={`py-2 px-4 text-xs font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer border-none ${activeTab === "holidays"
                ? "bg-[#133a69] text-white shadow-md shadow-blue-900/10 font-semibold"
                : "bg-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-200/50"
                }`}
            >
              <Calendar size={14} /> Holiday Management
            </button>
          </div>

          {/* Body Panels */}
          <div className="flex-1 p-6">
            {activeTab === "permissions" && (
              /* TAB 1: Member List with Toggle Permissions switches */
              <div className="flex flex-col h-full animate-[fadeIn_0.2s_ease-out]">
                <div className="relative mb-5">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
                  <input
                    type="text"
                    placeholder="Search employees by name..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-2xl text-xs focus:outline-none focus:border-[#133a69] focus:ring-2 focus:ring-blue-500/10 bg-slate-50/50 transition-all font-medium placeholder:text-slate-400"
                  />
                </div>

                <div className="my-2">
                  <DynamicTable
                    columns={columns}
                    rows={rows}
                    searchable={false}
                    height={420}
                    emptyComponent={emptyMemberComponent}
                  />
                </div>
              </div>
            )}

            {activeTab === "broadcast" && (
              /* TAB 2: Compose and dispatch Announcements */
              <form onSubmit={handleSendBroadcast} className="space-y-6 animate-[fadeIn_0.2s_ease-out]">
                <div className="bg-blue-50/50 border border-blue-100/50 rounded-2xl p-4 text-blue-900 text-xs font-semibold leading-relaxed shadow-xs flex gap-2.5 items-start">
                  <span className="text-base">📢</span>
                  <span><strong>WhatsApp-Style Broadcasts</strong>: Sending a broadcast will send a customized announcement message directly to the individual private conversation logs of all targeted recipients.</span>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">
                    Recipient Group Category
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <label className={`flex flex-col items-center justify-center p-5 border rounded-2xl cursor-pointer transition-all hover:scale-[1.01] hover:shadow-sm ${broadcastTarget === "all" ? "border-[#133a69] bg-gradient-to-br from-blue-50/40 to-blue-50/10 text-[#133a69] shadow-md shadow-blue-500/5" : "border-slate-200 bg-white hover:bg-slate-50/50 text-slate-600"}`}>
                      <input
                        type="radio"
                        name="target"
                        value="all"
                        checked={broadcastTarget === "all"}
                        onChange={() => setBroadcastTarget("all")}
                        className="sr-only"
                      />
                      <span className="text-sm font-extrabold">All Members</span>
                      <span className="text-[10px] text-slate-400 mt-1 text-center font-semibold">Send to everyone</span>
                    </label>
                    <label className={`flex flex-col items-center justify-center p-5 border rounded-2xl cursor-pointer transition-all hover:scale-[1.01] hover:shadow-sm ${broadcastTarget === "department" ? "border-[#133a69] bg-gradient-to-br from-blue-50/40 to-blue-50/10 text-[#133a69] shadow-md shadow-blue-500/5" : "border-slate-200 bg-white hover:bg-slate-50/50 text-slate-600"}`}>
                      <input
                        type="radio"
                        name="target"
                        value="department"
                        checked={broadcastTarget === "department"}
                        onChange={() => setBroadcastTarget("department")}
                        className="sr-only"
                      />
                      <span className="text-sm font-extrabold">By Department</span>
                      <span className="text-[10px] text-slate-400 mt-1 text-center font-semibold">Target specific role</span>
                    </label>
                    <label className={`flex flex-col items-center justify-center p-5 border rounded-2xl cursor-pointer transition-all hover:scale-[1.01] hover:shadow-sm ${broadcastTarget === "selected" ? "border-[#133a69] bg-gradient-to-br from-blue-50/40 to-blue-50/10 text-[#133a69] shadow-md shadow-blue-500/5" : "border-slate-200 bg-white hover:bg-slate-50/50 text-slate-600"}`}>
                      <input
                        type="radio"
                        name="target"
                        value="selected"
                        checked={broadcastTarget === "selected"}
                        onChange={() => setBroadcastTarget("selected")}
                        className="sr-only"
                      />
                      <span className="text-sm font-extrabold">Manually Select</span>
                      <span className="text-[10px] text-slate-400 mt-1 text-center font-semibold">Specific coworkers</span>
                    </label>
                  </div>
                </div>

                {/* Sub Options: Department Select */}
                {broadcastTarget === "department" && (
                  <div className="bg-slate-50/50 p-4 border border-slate-200/80 rounded-2xl animate-[fadeIn_0.15s_ease-out]">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2.5">
                      Choose Target Segment
                    </label>
                    <div className="flex gap-2.5">
                      {["Web", "Delphi", "Support"].map((dept) => (
                        <button
                          key={dept}
                          type="button"
                          onClick={() => setSelectedDept(dept)}
                          className={`px-4 py-2.5 text-xs font-bold rounded-xl border transition-all cursor-pointer ${selectedDept === dept
                            ? "bg-[#133a69] text-white border-[#133a69] shadow-md shadow-blue-900/10"
                            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                            }`}
                        >
                          {dept} Division
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sub Options: Recipient Checkbox List */}
                {broadcastTarget === "selected" && (
                  <div className="bg-slate-50/50 p-4 border border-slate-200/80 rounded-2xl animate-[fadeIn_0.15s_ease-out] max-h-[160px] overflow-y-auto">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                      Select Targets ({selectedRecipientIds.length} selected)
                    </label>
                    <div className="grid grid-cols-2 gap-2.5">
                      {members
                        .filter((m) => m._id !== currentUserId)
                        .map((m) => (
                          <label key={m._id} className="flex items-center gap-2.5 text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-100/50 p-1.5 rounded-xl transition-colors">
                            <input
                              type="checkbox"
                              checked={selectedRecipientIds.includes(m._id)}
                              onChange={() => handleRecipientCheckbox(m._id)}
                              className="rounded border-slate-350 text-[#133a69] focus:ring-[#133a69] w-4 h-4"
                            />
                            <span>{m.Name} ({m.Role.split(" ")[0]})</span>
                          </label>
                        ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                    Announcement Text Content
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Enter broadcast message details..."
                    value={broadcastText}
                    onChange={(e) => setBroadcastText(e.target.value)}
                    required
                    className="w-full p-4 border border-slate-200 rounded-2xl text-xs focus:outline-none focus:border-[#133a69] focus:ring-2 focus:ring-blue-500/10 bg-slate-50/30 transition-all font-medium placeholder:text-slate-400"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSendingBroadcast || !broadcastText.trim() || (broadcastTarget === "selected" && selectedRecipientIds.length === 0)}
                  className="w-full py-3.5 bg-gradient-to-br from-[#133a69] to-[#1e40af] hover:from-[#0f2d52] hover:to-[#1d4ed8] disabled:from-slate-300 disabled:to-slate-300 disabled:cursor-not-allowed text-white rounded-2xl text-xs font-bold shadow-md shadow-blue-500/10 transition-all hover:scale-[1.01] flex items-center justify-center gap-2 cursor-pointer border-none"
                >
                  {isSendingBroadcast ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Dispatching Broadcast...
                    </>
                  ) : (
                    <>
                      <Send size={14} /> Send Broadcast Message
                    </>
                  )}
                </button>
              </form>
            )}

            {activeTab === "groups" && (
              /* TAB 3: Create and Manage Group Channels (Split View) */
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 divide-y xl:divide-y-0 xl:divide-x divide-slate-100 animate-[fadeIn_0.2s_ease-out]">
                {/* Left Side: Create Group Form */}
                <form onSubmit={handleCreateGroup} className="space-y-5 pr-0 xl:pr-8">
                  <h3 className="text-sm font-extrabold text-slate-800 tracking-tight flex items-center gap-2 border-b border-slate-100 pb-3">
                    <Users size={16} className="text-[#133a69]" /> Create New Group
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                        Group Name
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Inhouse RND Team"
                        value={groupName}
                        onChange={(e) => {
                          setGroupName(e.target.value);
                          setGroupNameTouched(true);
                        }}
                        onBlur={() => setGroupNameTouched(true)}
                        required
                        className={`w-full p-3 border rounded-2xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/10 bg-slate-50/30 transition-all font-medium placeholder:text-slate-400 ${groupNameTouched && !groupName.trim() ? "border-rose-450 focus:border-rose-500" : "border-slate-200 focus:border-[#133a69]"}`}
                      />
                      {groupNameTouched && !groupName.trim() && (
                        <p className="text-[10px] text-rose-500 font-bold mt-1.5 flex items-center gap-1">
                          ⚠️ Group name is required.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                        Group Image URL (Optional)
                      </label>
                      <input
                        type="text"
                        placeholder="https://example.com/avatar.png"
                        value={groupImage}
                        onChange={(e) => setGroupImage(e.target.value)}
                        className="w-full p-3 border border-slate-200 focus:border-[#133a69] focus:ring-2 focus:ring-blue-500/10 rounded-2xl text-xs bg-slate-50/30 transition-all font-medium placeholder:text-slate-400"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                        Select Group Members ({selectedGroupMemberIds.length} selected)
                      </label>
                      <div className="bg-slate-50/50 p-4 border border-slate-200/80 rounded-2xl max-h-[160px] overflow-y-auto grid grid-cols-1 gap-2.5">
                        {members
                          .filter((m) => m._id !== currentUserId)
                          .map((m) => (
                            <label key={m._id} className="flex items-center gap-2.5 text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-100/50 p-1.5 rounded-xl transition-colors">
                              <input
                                type="checkbox"
                                checked={selectedGroupMemberIds.includes(m._id)}
                                onChange={() => handleGroupCheckbox(m._id)}
                                className="rounded border-slate-350 text-[#133a69] focus:ring-[#133a69] w-4 h-4"
                              />
                              <span>{m.Name} ({m.Role.split(" ")[0]})</span>
                            </label>
                          ))}
                      </div>
                    </div>
                  </div>

                  {selectedGroupMemberIds.length < 2 && (
                    <p className="text-[10px] text-rose-500 font-bold mb-2.5 flex items-center gap-1">
                      ⚠️ Please select at least 2 members.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isCreatingGroup || !groupName.trim() || selectedGroupMemberIds.length < 2}
                    className="w-full py-3.5 bg-[#133a69] hover:bg-[#0f2d52] disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-2xl text-xs font-bold shadow-md shadow-blue-500/10 transition-all hover:scale-[1.01] flex items-center justify-center gap-2 cursor-pointer border-none"
                  >
                    {isCreatingGroup ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Creating...
                      </>
                    ) : (
                      <>
                        <Users size={14} /> Create Group Channel
                      </>
                    )}
                  </button>
                </form>

                {/* Right Side: Manage / Delete Existing Groups */}
                <div className="space-y-5 pt-6 xl:pt-0 xl:pl-8">
                  {(() => {
                    const dynamicMgmtMember = members.find(
                      (m) => m.Email === "management@dynamic.com" || m.Name === "Dynamic Management"
                    );

                    const groupConversations = conversations.filter((c) => c.isGroup);

                    // Identify the Dynamic Management channel among group conversations without duplicating
                    const allGroupsToDisplay = groupConversations.map((group) => {
                      const isDefault =
                        group.name?.toLowerCase().includes("dynamic management") ||
                        (group.participants &&
                          group.participants.some(
                            (p) =>
                              (p.Email || p.email) === "management@dynamic.com" ||
                              (p._id || p)?.toString() === dynamicMgmtMember?._id?.toString()
                          ));
                      return {
                        ...group,
                        isDefaultSender: Boolean(isDefault)
                      };
                    });

                    // Fallback only if no group exists in state yet
                    if (allGroupsToDisplay.length === 0 && dynamicMgmtMember) {
                      allGroupsToDisplay.push({
                        _id: dynamicMgmtMember._id,
                        name: "Dynamic Management",
                        groupImage: dynamicMgmtMember.ProfilePhoto || defaultProfile,
                        isDefaultSender: true,
                        participants: []
                      });
                    }

                    return (
                      <>
                        <h3 className="text-sm font-extrabold text-slate-800 tracking-tight flex items-center gap-2 border-b border-slate-100 pb-3">
                          <Users size={16} className="text-slate-500" /> Active Group Channels ({allGroupsToDisplay.length})
                        </h3>

                        <div className="max-h-[360px] overflow-y-auto space-y-3 pr-2 scrollbar-custom">
                          {allGroupsToDisplay.length > 0 ? (
                            allGroupsToDisplay.map((group) => (
                              <div
                                key={group._id}
                                className="flex items-center justify-between p-3.5 bg-slate-50/40 hover:bg-slate-50 border border-slate-200/65 rounded-2xl transition-all hover:shadow-xs"
                              >
                                <div className="flex items-center gap-3">
                                  <img
                                    src={group.groupImage || defaultProfile}
                                    alt={group.name}
                                    className="w-10 h-10 rounded-2xl border object-cover shadow-xs shrink-0"
                                    onError={(e) => {
                                      e.target.src = defaultProfile;
                                    }}
                                  />
                                  <div className="min-w-0">
                                    <span className="text-xs font-bold text-slate-800 block truncate">
                                      {group.name}
                                    </span>
                                    <span className="text-[10px] text-slate-450 font-semibold block mt-0.5">
                                      {group.isDefaultSender ? `Dynamic Management • ${group.participants?.length || 0} participants` : `${group.participants?.length || 0} participants`}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleOpenEditModal(group)}
                                    className="p-2 text-[#133a69] hover:bg-blue-50 hover:text-blue-700 rounded-xl transition-all border-none bg-transparent cursor-pointer"
                                    title={group.isDefaultSender ? "Edit Channel Name" : "Edit Members"}
                                  >
                                    {group.isDefaultSender ? <Shield size={14} /> : <Users size={14} />}
                                  </button>
                                  {!group.isDefaultSender && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteGroup(group._id)}
                                      className="p-2 text-rose-500 hover:bg-rose-50 hover:text-rose-700 rounded-xl transition-all border-none bg-transparent cursor-pointer"
                                      title="Delete Group Channel"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="flex flex-col items-center justify-center p-8 border border-dashed border-slate-200 rounded-2xl text-slate-400">
                              <Users className="w-8 h-8 mb-2 opacity-40" />
                              <p className="text-xs font-semibold">No group channels exist yet</p>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {activeTab === "history" && !isAuditUnlocked && (
              <div className="flex flex-col items-center justify-center min-h-[420px] bg-slate-50/30 rounded-2xl border border-slate-200/80 p-8 text-center animate-[fadeIn_0.2s_ease-out] max-w-sm mx-auto my-12 shadow-sm">
                <div className="w-14 h-14 bg-blue-50 text-[#133a69] rounded-2xl flex items-center justify-center mb-5 border border-blue-100 shadow-xs">
                  <Shield size={24} />
                </div>
                <h3 className="text-sm font-extrabold text-slate-800 tracking-tight">Security Check</h3>
                <p className="text-xs text-slate-500 font-semibold mt-2 max-w-[260px] leading-relaxed">
                  Enter the 3-digit security PIN to unlock the Member Chat Audit database.
                </p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (auditPin === "963") {
                      setIsAuditUnlocked(true);
                      setPinError("");
                    } else {
                      setPinError("⚠️ Invalid Security PIN. Access Denied.");
                      setAuditPin("");
                    }
                  }}
                  className="w-full mt-6 space-y-4"
                >
                  <input
                    type="password"
                    placeholder="Security PIN"
                    value={auditPin}
                    onChange={(e) => setAuditPin(e.target.value)}
                    maxLength={10}
                    autoFocus
                    className="w-full text-center tracking-[0.5em] font-black text-base p-3 border border-slate-200 focus:border-[#133a69] focus:ring-2 focus:ring-blue-500/10 rounded-2xl bg-white focus:outline-none transition-all placeholder:tracking-normal placeholder:font-semibold placeholder:text-xs placeholder:text-slate-400"
                  />
                  {pinError && (
                    <p className="text-[10px] text-rose-500 font-bold">
                      {pinError}
                    </p>
                  )}
                  <button
                    type="submit"
                    className="w-full py-3 bg-gradient-to-br from-[#133a69] to-[#1e40af] hover:from-[#0f2d52] hover:to-[#1d4ed8] text-white rounded-2xl text-xs font-bold shadow-md shadow-blue-500/10 transition-all hover:scale-[1.01] cursor-pointer border-none"
                  >
                    Verify & Unlock
                  </button>
                </form>
              </div>
            )}

            {activeTab === "history" && isAuditUnlocked && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[580px] overflow-hidden animate-[fadeIn_0.2s_ease-out]">
                {/* Column 1: Member List */}
                <div className="lg:col-span-3 border-r border-slate-100 pr-4 flex flex-col h-full overflow-hidden">
                  <div className="flex items-center justify-between mb-3 shrink-0">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Select Member
                    </h3>
                    <button
                      onClick={() => {
                        setIsAuditUnlocked(false);
                        setAuditPin("");
                        setSelectedAuditMember(null);
                        setSelectedAuditConversation(null);
                      }}
                      className="text-[9px] text-rose-500 hover:text-rose-700 bg-rose-50 hover:bg-rose-100/50 px-2.5 py-1 rounded-lg border-none cursor-pointer font-bold transition-colors flex items-center gap-1"
                      title="Lock Audit Panel"
                    >
                      <Shield size={10} /> Lock Panel
                    </button>
                  </div>
                  <div className="relative mb-3.5 shrink-0">
                    <Search className="w-3.5 h-3.5 text-slate-450 absolute left-3 top-3.5" />
                    <input
                      type="text"
                      placeholder="Search member..."
                      value={auditMemberSearch}
                      onChange={(e) => setAuditMemberSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 border border-slate-200 focus:border-[#133a69] focus:ring-2 focus:ring-blue-500/10 rounded-2xl text-xs bg-slate-50/50 transition-all font-medium placeholder:text-slate-400"
                    />
                  </div>
                  <div className="flex-1 overflow-y-auto space-y-2 pr-1 scrollbar-custom">
                    {members
                      .filter((m) =>
                        m.Name.toLowerCase().includes(auditMemberSearch.toLowerCase())
                      )
                      .filter((m) => {
                        return auditConvCounts[m._id] === undefined || auditConvCounts[m._id] > 0;
                      })
                      .map((member) => (
                        <div
                          key={member._id}
                          onClick={() => handleSelectAuditMember(member)}
                          className={`flex items-center gap-3 p-2.5 rounded-2xl cursor-pointer transition-all border hover:-translate-y-[1px] ${selectedAuditMember?._id === member._id
                            ? "bg-gradient-to-br from-blue-50/50 to-blue-50/20 border-blue-200 text-[#133a69] shadow-sm"
                            : "border-transparent hover:bg-slate-50 hover:border-slate-100 text-slate-700"
                            }`}
                        >
                          <img
                            src={member.ProfilePhoto || defaultProfile}
                            alt={member.Name}
                            className="w-8 h-8 rounded-xl object-cover border shadow-xs shrink-0"
                            onError={(e) => {
                              e.target.src = defaultProfile;
                            }}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-1">
                              <p className="text-xs font-bold truncate leading-tight">{member.Name}</p>
                              {auditConvCounts[member._id] !== undefined ? (
                                <span className="text-[9px] bg-blue-50 text-[#133a69] border border-blue-100 px-1.5 py-0.5 rounded-lg font-bold shrink-0">
                                  {auditConvCounts[member._id]} {auditConvCounts[member._id] === 1 ? "person" : "people"}
                                </span>
                              ) : (
                                <span className="text-[8px] text-slate-400 animate-pulse font-medium">loading...</span>
                              )}
                            </div>
                            <p className="text-[10px] text-slate-450 truncate mt-0.5">{member.Role}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Column 2: Conversation List */}
                <div className="lg:col-span-4 border-r border-slate-100 px-4 flex flex-col h-full overflow-hidden">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                    Conversations
                  </h3>
                  {!selectedAuditMember ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-4 text-slate-450 bg-slate-50/30 rounded-3xl border border-dashed border-slate-200">
                      <Users className="w-8 h-8 mb-2 opacity-35 text-[#133a69]" />
                      <p className="text-xs font-bold">Select a member to audit</p>
                      <p className="text-[10px] text-slate-400 mt-1 max-w-[180px]">Choose an employee from the left panel to inspect their active conversations.</p>
                    </div>
                  ) : auditConversationsLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-[#133a69] animate-spin" />
                    </div>
                  ) : auditConversations.filter((conv) => conv.lastMessage).length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-4 text-slate-450 bg-slate-50/30 rounded-3xl border border-dashed border-slate-200">
                      <MessageSquare className="w-8 h-8 mb-2 opacity-35 text-[#133a69]" />
                      <p className="text-xs font-bold">No conversations found</p>
                      <p className="text-[10px] text-slate-400 mt-1 max-w-[180px]">This member does not have any active chats yet.</p>
                    </div>
                  ) : (
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1 scrollbar-custom">
                      {auditConversations
                        .filter((conv) => conv.lastMessage)
                        .map((conv) => {
                          const details = getAuditConversationDetails(conv, selectedAuditMember._id);
                          return (
                            <div
                              key={conv._id}
                              onClick={() => handleSelectAuditConversation(conv)}
                              className={`flex items-start gap-3 p-3 rounded-2xl cursor-pointer transition-all border hover:-translate-y-[1px] ${selectedAuditConversation?._id === conv._id
                                ? "bg-gradient-to-br from-blue-50/50 to-blue-50/20 border-blue-200 text-[#133a69] shadow-sm"
                                : "border-transparent hover:bg-slate-50 hover:border-slate-100 text-slate-700"
                                }`}
                            >
                              <img
                                src={details.image}
                                alt={details.name}
                                className="w-8 h-8 rounded-xl object-cover border shadow-xs mt-0.5 shrink-0"
                                onError={(e) => {
                                  e.target.src = defaultProfile;
                                }}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-1">
                                  <span className="text-xs font-bold truncate">{details.name}</span>
                                  <span className="text-[8px] bg-slate-100 px-2 py-0.5 rounded-lg text-slate-500 shrink-0 font-bold uppercase tracking-wider">
                                    {details.type}
                                  </span>
                                </div>
                                <p className="text-[10px] text-slate-450 truncate mt-1">
                                  {conv.lastMessage?.messageType === "image"
                                    ? "📷 Attachment"
                                    : conv.lastMessage?.message || "No messages yet"}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>

                {/* Column 3: Message History */}
                <div className="lg:col-span-5 pl-4 flex flex-col h-full overflow-hidden">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                    Message Logs
                  </h3>
                  {!selectedAuditConversation ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-4 text-slate-450 bg-slate-50/30 rounded-3xl border border-dashed border-slate-200">
                      <MessageSquare className="w-8 h-8 mb-2 opacity-35 text-[#133a69]" />
                      <p className="text-xs font-bold">Select a conversation</p>
                      <p className="text-[10px] text-slate-400 mt-1 max-w-[180px]">Choose a conversation to view the full message history logs.</p>
                    </div>
                  ) : auditMessagesLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-[#133a69] animate-spin" />
                    </div>
                  ) : auditMessages.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-4 text-slate-450 bg-slate-50/30 rounded-3xl border border-dashed border-slate-200">
                      <MessageSquare className="w-8 h-8 mb-2 opacity-35 text-[#133a69]" />
                      <p className="text-xs font-bold">No messages found</p>
                      <p className="text-[10px] text-slate-400 mt-1">There are no messages in this chat conversation.</p>
                    </div>
                  ) : (
                    <div ref={auditMessagesContainerRef} className="flex-1 overflow-y-auto p-4 bg-slate-50/50 border border-slate-200/80 rounded-3xl space-y-4 scrollbar-custom">
                      {auditMessages.map((msg, index) => {
                        const isSelectedMember = msg.sender?._id === selectedAuditMember._id;

                        // Determine if we should show a date separator
                        const currentDate = new Date(msg.createdAt).toLocaleDateString([], {
                          year: "numeric",
                          month: "long",
                          day: "numeric"
                        });
                        const previousDate = index > 0
                          ? new Date(auditMessages[index - 1].createdAt).toLocaleDateString([], {
                            year: "numeric",
                            month: "long",
                            day: "numeric"
                          })
                          : null;

                        const showDateSeparator = currentDate !== previousDate;

                        return (
                          <React.Fragment key={msg._id || index}>
                            {showDateSeparator && (
                              <div className="flex justify-center my-3.5 shrink-0">
                                <span className="bg-white/90 backdrop-blur-xs text-slate-500 text-[9px] font-bold px-3 py-1 rounded-xl uppercase tracking-wider shadow-xs border border-slate-200/50">
                                  {currentDate}
                                </span>
                              </div>
                            )}
                            <div
                              id={`audit-msg-${msg._id}`}
                              className={`flex items-end gap-1.5 ${isSelectedMember ? "justify-end" : "justify-start"}`}
                              style={{ marginBottom: msg.reactions && msg.reactions.length > 0 ? "14px" : "0px" }}
                            >
                              {!isSelectedMember && (
                                <img
                                  src={msg.sender?.ProfilePhoto || defaultProfile}
                                  alt={msg.sender?.Name || "User"}
                                  className="w-6 h-6 rounded-full object-cover border shadow-xs shrink-0 mb-1"
                                  onError={(e) => {
                                    e.target.src = defaultProfile;
                                  }}
                                  title={msg.sender?.Name || "User"}
                                />
                              )}
                              <div
                                className={`max-w-[75%] rounded-xl shadow-xs leading-snug relative min-w-[80px] px-2.5 py-1.5 pb-5 border break-words ${isSelectedMember
                                  ? "bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] text-white border-none rounded-tr-none shadow-sm"
                                  : "bg-white text-slate-800 border-slate-200/75 rounded-tl-none shadow-xs"
                                  }`}
                                style={msg.replyTo ? { minWidth: "200px" } : {}}
                              >
                                {msg.isForwarded && (
                                  <span style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    fontSize: "9px",
                                    color: isSelectedMember ? "rgba(255,255,255,0.7)" : "#64748b",
                                    fontStyle: "italic",
                                    marginBottom: "4px"
                                  }}>
                                    <CornerUpRight size={10} /> Forwarded
                                  </span>
                                )}

                                {msg.replyTo && (
                                  <div
                                    onClick={() => scrollToAuditMessage(msg.replyTo.messageId)}
                                    style={{
                                      backgroundColor: isSelectedMember ? "rgba(255, 255, 255, 0.15)" : "#f1f5f9",
                                      borderLeft: `3px solid ${isSelectedMember ? "#fff" : "#1e3a8a"}`,
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      marginBottom: "6px",
                                      cursor: "pointer",
                                      fontSize: "11px",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "2px",
                                      maxWidth: "100%",
                                      overflow: "hidden"
                                    }}
                                  >
                                    <span style={{
                                      fontWeight: 700,
                                      color: isSelectedMember ? "#fff" : "#1e3a8a",
                                      fontSize: "10px"
                                    }}>
                                      {msg.replyTo.senderName}
                                    </span>
                                    <span style={{
                                      color: isSelectedMember ? "rgba(255, 255, 255, 0.85)" : "#475569",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis"
                                    }}>
                                      {msg.replyTo.messageType === "image" ? "📷 Image" : msg.replyTo.messageType === "file" ? "📁 Document" : msg.replyTo.message}
                                    </span>
                                  </div>
                                )}
                                {msg.messageType === "image" ? (
                                  <div className="flex flex-col">
                                    <div
                                      className="relative rounded-lg overflow-hidden bg-black/5 mb-1 flex items-center justify-center cursor-pointer hover:opacity-90 transition-all"
                                      style={{ maxWidth: "160px", maxHeight: "120px" }}
                                      onClick={() => setLightboxImage(msg.message)}
                                    >
                                      <img
                                        src={msg.message}
                                        alt="Chat Attachment"
                                        className="block object-cover"
                                        style={{ width: "100%", height: "100%", maxWidth: "160px", maxHeight: "120px" }}
                                      />
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadImage(msg.message);
                                        }}
                                        className="absolute top-1 right-1 bg-slate-950/70 hover:bg-[#133a69] text-white p-1 rounded cursor-pointer transition-all hover:scale-105 shadow-sm border-none"
                                        title="Download Image"
                                      >
                                        <Download size={10} />
                                      </button>
                                    </div>
                                    {msg.caption && (
                                      <div className="font-semibold text-[11px] leading-snug mt-1 break-words">
                                        {parseMessageMarkdown(msg.caption, isSelectedMember)}
                                      </div>
                                    )}
                                  </div>
                                ) : msg.messageType === "file" ? (
                                  <div className="flex flex-col">
                                    <div className="saas-file-attachment-card flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-2 max-w-[270px]">
                                      <div className="p-2 bg-blue-50 text-blue-600 rounded-lg shrink-0">
                                        <FileText size={24} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-[11px] font-bold text-slate-800 truncate m-0">
                                          {getFileName(msg.message)}
                                        </p>
                                        <div className="flex items-center gap-1 flex-wrap mt-0.5">
                                          <span className="text-[9px] text-slate-400 uppercase font-semibold">
                                            {getFileExtension(msg.message)} Document
                                          </span>
                                          {(msg.fileSize || msg.totalFileSize) && (
                                            <>
                                              <span className="text-[9px] text-slate-300">•</span>
                                              <span className="text-[9px] text-slate-600 font-bold">
                                                {formatFileSize(msg.fileSize || msg.totalFileSize)}
                                              </span>
                                            </>
                                          )}
                                          {msg.fileCount > 1 && (
                                            <span className="text-[8px] bg-blue-100 text-blue-700 font-bold px-1 rounded">
                                              {msg.fileCount} Files
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex flex-col gap-1 shrink-0">
                                        <button
                                          type="button"
                                          onClick={() => window.open(msg.message, "_blank")}
                                          className="p-1 bg-blue-600 hover:bg-blue-700 text-white rounded border-none cursor-pointer transition-all flex items-center justify-center"
                                          title="View/Open File"
                                        >
                                          <ExternalLink size={10} />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDownloadImage(msg.message)}
                                          className="p-1 bg-slate-600 hover:bg-slate-700 text-white rounded border-none cursor-pointer transition-all flex items-center justify-center"
                                          title="Download File"
                                        >
                                          <Download size={10} />
                                        </button>
                                      </div>
                                    </div>
                                    {msg.caption && (
                                      <div className="font-semibold text-[11px] leading-snug mt-1 break-words">
                                        {parseMessageMarkdown(msg.caption, isSelectedMember)}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="font-semibold text-[11px] leading-snug m-0 break-words">
                                    {parseMessageMarkdown(msg.message, isSelectedMember)}
                                  </div>
                                )}
                                <div
                                  className={`text-[7.5px] font-semibold absolute bottom-1 right-2.5 ${isSelectedMember ? "text-white/60" : "text-slate-400"
                                    }`}
                                >
                                  {new Date(msg.createdAt).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit"
                                  })}
                                </div>

                                {/* Reactions display */}
                                {msg.reactions && msg.reactions.length > 0 && (
                                  <div style={{
                                    position: "absolute",
                                    bottom: "-12px",
                                    [isSelectedMember ? "right" : "left"]: "12px",
                                    display: "flex",
                                    gap: "2px",
                                    backgroundColor: "white",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "12px",
                                    padding: "2px 6px",
                                    fontSize: "11px",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                    zIndex: 2,
                                    color: "#000"
                                  }}>
                                    {Object.entries(msg.reactions.reduce((acc, r) => {
                                      acc[r.emoji] = (acc[r.emoji] || 0) + 1;
                                      return acc;
                                    }, {})).map(([emoji, count]) => (
                                      <span key={emoji} style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                                        {emoji} <span style={{ fontSize: "9px", color: "#64748b" }}>{count > 1 ? count : ""}</span>
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {isSelectedMember && (
                                <img
                                  src={msg.sender?.ProfilePhoto || defaultProfile}
                                  alt={msg.sender?.Name || "User"}
                                  className="w-6 h-6 rounded-full object-cover border shadow-xs shrink-0 mb-1"
                                  onError={(e) => {
                                    e.target.src = defaultProfile;
                                  }}
                                  title={msg.sender?.Name || "User"}
                                />
                              )}
                            </div>
                          </React.Fragment>
                        );
                      })}
                      <div style={{ height: "16px", width: "100%", clear: "both" }} />
                      <div ref={auditMessagesEndRef} style={{ height: "1px", width: "100%", clear: "both" }} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "test-notifications" && (
              <div className="flex flex-col h-full space-y-6 animate-[fadeIn_0.2s_ease-out]">
                {/* Top Action Panels Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left: Scheduler Configuration Settings Card (In-Time) */}
                  <div className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-5 flex flex-col justify-between shadow-sm min-h-[180px]">
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-xs font-extrabold text-slate-700 tracking-wide uppercase flex items-center gap-1.5 m-0">
                          <Shield size={14} className="text-blue-600" /> Daily Scheduler (In-Time)
                        </h4>

                        {/* Toggle switch */}
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-black uppercase tracking-wider ${scheduleEnabled ? "text-emerald-600" : "text-slate-400"}`}>
                            {scheduleEnabled ? "Active" : "Disabled"}
                          </span>
                          <button
                            type="button"
                            onClick={handleToggleScheduleEnabled}
                            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${scheduleEnabled ? "bg-emerald-500" : "bg-slate-300"
                              }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${scheduleEnabled ? "translate-x-4" : "translate-x-0"
                                }`}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Time Input & Add Button */}
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">
                            Add Daily Time (IST)
                          </label>
                          <input
                            type="time"
                            value={newScheduleTime}
                            onChange={(e) => setNewScheduleTime(e.target.value)}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-[#133a69] focus:ring-2 focus:ring-blue-500/10 bg-white transition-all shadow-sm text-slate-700"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddScheduleTime}
                          className="h-[38px] px-3.5 bg-blue-50 hover:bg-blue-100 text-[#133a69] rounded-xl font-black text-sm flex items-center justify-center cursor-pointer border-none shadow-sm transition-all self-end"
                          title="Add execution time"
                        >
                          +
                        </button>
                      </div>

                      {/* Display schedule times list tags */}
                      <div className="flex flex-wrap gap-2 max-h-[100px] overflow-y-auto py-1.5 pr-1 border border-dashed border-slate-200 rounded-xl p-2 bg-white/50 min-h-[46px] items-center">
                        {scheduleTimes.length > 0 ? (
                          scheduleTimes.map((timeVal, idx) => {
                            const status = getTimeStatus(timeVal);
                            let statusClass = "bg-slate-100 text-slate-600 border-slate-200";
                            let dotClass = "bg-slate-400";
                            let statusText = "Pending";

                            if (status === "success") {
                              statusClass = "bg-emerald-50/90 text-emerald-800 border-emerald-200";
                              dotClass = "bg-emerald-500";
                              statusText = "Fired";
                            } else if (status === "partial") {
                              statusClass = "bg-amber-50/90 text-amber-800 border-amber-250";
                              dotClass = "bg-amber-500";
                              statusText = "Partial";
                            } else if (status === "failed") {
                              statusClass = "bg-rose-50/90 text-rose-800 border-rose-200";
                              dotClass = "bg-rose-500";
                              statusText = "Failed";
                            }

                            return (
                              <div
                                key={idx}
                                className={`text-[10px] border px-2 py-1 rounded-lg font-extrabold flex items-center gap-1.5 transition-all shadow-2xs hover:scale-[1.02] ${statusClass}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${dotClass} shrink-0`} />
                                <span>{timeVal}</span>
                                <span className="text-[8px] font-bold opacity-75 uppercase">({statusText})</span>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteScheduleTime(timeVal)}
                                  className="hover:text-rose-600 font-extrabold text-[9px] cursor-pointer bg-transparent border-none p-0.5 ml-0.5 flex items-center justify-center text-slate-400"
                                  title="Remove time"
                                >
                                  ✕
                                </button>
                              </div>
                            );
                          })
                        ) : (
                          <span className="text-[10px] text-slate-400 font-medium italic block pl-1.5">No schedule times added yet.</span>
                        )}
                      </div>

                      {/* Recipient Configuration Trigger Button */}
                      <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col gap-1.5">
                        <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider ml-0.5">Recipients</span>
                        <button
                          type="button"
                          onClick={() => {
                            setTempInTimeExcluded(inTimeExcludedEmployees);
                            setIsInTimeModalOpen(true);
                          }}
                          className="w-full py-2 bg-slate-100 hover:bg-slate-200/80 text-slate-700 rounded-xl text-xs font-bold transition-all border border-slate-200/50 flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <Users size={13} className="text-slate-500" />
                          Configure Recipients ({members.filter(m => m.Email !== "management@dynamic.com").length - inTimeExcludedEmployees.length} selected)
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={saveScheduleSettings}
                      disabled={isSavingScheduleTime}
                      className="mt-4 w-full py-2.5 bg-[#133a69] hover:bg-[#0f2d52] text-white rounded-xl text-xs font-bold shadow-sm transition-all hover:scale-[1.01] cursor-pointer border-none flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingScheduleTime ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check size={14} />}
                      Save In-Time Settings
                    </button>
                  </div>

                  {/* Middle: Scheduler Configuration Settings Card (Out-Time) */}
                  <div className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-5 flex flex-col justify-between shadow-sm min-h-[180px]">
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-xs font-extrabold text-slate-700 tracking-wide uppercase flex items-center gap-1.5 m-0">
                          <Shield size={14} className="text-blue-600" /> Daily Scheduler (Out-Time)
                        </h4>

                        {/* Toggle switch */}
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-black uppercase tracking-wider ${checkoutEnabled ? "text-emerald-600" : "text-slate-400"}`}>
                            {checkoutEnabled ? "Active" : "Disabled"}
                          </span>
                          <button
                            type="button"
                            onClick={handleToggleCheckoutEnabled}
                            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${checkoutEnabled ? "bg-emerald-500" : "bg-slate-300"
                              }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${checkoutEnabled ? "translate-x-4" : "translate-x-0"
                                }`}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Time Input & Add Button */}
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">
                            Add Out Time (IST)
                          </label>
                          <input
                            type="time"
                            value={newCheckoutTime}
                            onChange={(e) => setNewCheckoutTime(e.target.value)}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-[#133a69] focus:ring-2 focus:ring-blue-500/10 bg-white transition-all shadow-sm text-slate-700"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddCheckoutTime}
                          className="h-[38px] px-3.5 bg-blue-50 hover:bg-blue-100 text-[#133a69] rounded-xl font-black text-sm flex items-center justify-center cursor-pointer border-none shadow-sm transition-all self-end"
                          title="Add execution time"
                        >
                          +
                        </button>
                      </div>

                      {/* Display schedule times list tags */}
                      <div className="flex flex-wrap gap-2 max-h-[100px] overflow-y-auto py-1.5 pr-1 border border-dashed border-slate-200 rounded-xl p-2 bg-white/50 min-h-[46px] items-center">
                        {checkoutTimes.length > 0 ? (
                          checkoutTimes.map((timeVal, idx) => {
                            const status = getCheckoutTimeStatus(timeVal);
                            let statusClass = "bg-slate-100 text-slate-600 border-slate-200";
                            let dotClass = "bg-slate-400";
                            let statusText = "Pending";

                            if (status === "success") {
                              statusClass = "bg-emerald-50/90 text-emerald-800 border-emerald-200";
                              dotClass = "bg-emerald-500";
                              statusText = "Fired";
                            } else if (status === "partial") {
                              statusClass = "bg-amber-50/90 text-amber-800 border-amber-250";
                              dotClass = "bg-amber-500";
                              statusText = "Partial";
                            } else if (status === "failed") {
                              statusClass = "bg-rose-50/90 text-rose-800 border-rose-200";
                              dotClass = "bg-rose-500";
                              statusText = "Failed";
                            }

                            return (
                              <div
                                key={idx}
                                className={`text-[10px] border px-2 py-1 rounded-lg font-extrabold flex items-center gap-1.5 transition-all shadow-2xs hover:scale-[1.02] ${statusClass}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${dotClass} shrink-0`} />
                                <span>{timeVal}</span>
                                <span className="text-[8px] font-bold opacity-75 uppercase">({statusText})</span>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteCheckoutTime(timeVal)}
                                  className="hover:text-rose-600 font-extrabold text-[9px] cursor-pointer bg-transparent border-none p-0.5 ml-0.5 flex items-center justify-center text-slate-400"
                                  title="Remove time"
                                >
                                  ✕
                                </button>
                              </div>
                            );
                          })
                        ) : (
                          <span className="text-[10px] text-slate-400 font-medium italic block pl-1.5">No schedule times added yet.</span>
                        )}
                      </div>

                      {/* Recipient Configuration Trigger Button */}
                      <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col gap-1.5">
                        <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider ml-0.5">Recipients</span>
                        <button
                          type="button"
                          onClick={() => {
                            setTempOutTimeExcluded(outTimeExcludedEmployees);
                            setIsOutTimeModalOpen(true);
                          }}
                          className="w-full py-2 bg-slate-100 hover:bg-slate-200/80 text-slate-700 rounded-xl text-xs font-bold transition-all border border-slate-200/50 flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <Users size={13} className="text-slate-500" />
                          Configure Recipients ({members.filter(m => m.Email !== "management@dynamic.com").length - outTimeExcludedEmployees.length} selected)
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={saveCheckoutScheduleSettings}
                      disabled={isSavingCheckoutTime}
                      className="mt-4 w-full py-2.5 bg-[#133a69] hover:bg-[#0f2d52] text-white rounded-xl text-xs font-bold shadow-sm transition-all hover:scale-[1.01] cursor-pointer border-none flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingCheckoutTime ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check size={14} />}
                      Save Checkout Settings
                    </button>
                  </div>

                  {/* Right Column: Stacked Actions */}
                  <div className="flex flex-col gap-6">
                    {/* Manual Dispatch override trigger */}
                    <div className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-5 flex flex-col justify-between shadow-sm flex-1">
                      <div>
                        <h4 className="text-xs font-extrabold text-slate-700 tracking-wide uppercase mb-3 flex items-center gap-1.5">
                          <Send size={14} className="text-indigo-600" /> Manual Notification Dispatch
                        </h4>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">
                          Select Target Date
                        </label>
                        <input
                          type="date"
                          value={testNotificationDate}
                          onChange={handleDateChange}
                          max={new Date().toISOString().split("T")[0]}
                          className="w-full px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-[#133a69] focus:ring-2 focus:ring-blue-500/10 bg-white transition-all shadow-sm text-slate-700"
                        />
                      </div>
                      <div className="flex gap-3 mt-4">
                        <button
                          type="button"
                          onClick={() => handleTestNotificationAll("in")}
                          disabled={!!testNotificationLoading}
                          className="flex-1 py-2 bg-gradient-to-br from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white rounded-xl text-[10px] font-extrabold shadow-sm transition-all hover:scale-[1.01] cursor-pointer border-none flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {testNotificationLoading === "in" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send size={12} />}
                          Trigger In-Time
                        </button>
                        <button
                          type="button"
                          onClick={() => handleTestNotificationAll("out")}
                          disabled={!!testNotificationLoading}
                          className="flex-1 py-2 bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white rounded-xl text-[10px] font-extrabold shadow-sm transition-all hover:scale-[1.01] cursor-pointer border-none flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {testNotificationLoading === "out" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send size={12} />}
                          Trigger Out-Time
                        </button>
                      </div>
                    </div>

                    {/* Chat Settings Card */}
                    <div className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-5 flex flex-col justify-between shadow-sm min-h-[180px] flex-1">
                      <div>
                        <h4 className="text-xs font-extrabold text-slate-700 tracking-wide uppercase mb-3 flex items-center gap-1.5 m-0">
                          <Download size={14} className="text-blue-600" /> Chat Settings
                        </h4>
                        <div className="mt-3">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">
                            Max File Size Limit (MB)
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="500"
                            value={chatFileSizeLimitInput}
                            onChange={(e) => setChatFileSizeLimitInput(e.target.value)}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-[#133a69] focus:ring-2 focus:ring-blue-500/10 bg-white transition-all shadow-sm text-slate-700"
                            placeholder="e.g. 25"
                          />
                          <p className="text-[9px] text-slate-400 mt-2 italic leading-relaxed">
                            Sets the maximum size in megabytes for all document and image uploads across all chats.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={saveChatFileSizeLimit}
                        disabled={isSavingFileSizeLimit}
                        className="mt-4 w-full py-2.5 bg-[#133a69] hover:bg-[#0f2d52] text-white rounded-xl text-xs font-bold shadow-sm transition-all hover:scale-[1.01] cursor-pointer border-none flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSavingFileSizeLimit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check size={14} />}
                        Save Chat Settings
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sub-tabs / Logs Toggle Section */}
                <div className="flex-1 border border-slate-200/80 rounded-2xl bg-white overflow-hidden flex flex-col min-h-[360px] shadow-xs">
                  {/* Logs Tab Selector */}
                  <div className="bg-slate-50/70 border-b border-slate-200 px-5 py-2 flex items-center justify-between shrink-0">
                    <div className="flex gap-4">
                      <button
                        onClick={() => setLogsSubTab("sent-messages")}
                        className={`py-2 text-xs font-bold border-b-2 transition-all cursor-pointer bg-transparent border-none ${logsSubTab === "sent-messages"
                          ? "border-[#133a69] text-[#133a69]"
                          : "border-transparent text-slate-400 hover:text-slate-650"
                          }`}
                      >
                        Sent Logs ({attendanceLogs.length})
                      </button>
                      <button
                        onClick={() => setLogsSubTab("schedule-runs")}
                        className={`py-2 text-xs font-bold border-b-2 transition-all cursor-pointer bg-transparent border-none ${logsSubTab === "schedule-runs"
                          ? "border-[#133a69] text-[#133a69]"
                          : "border-transparent text-slate-400 hover:text-slate-650"
                          }`}
                      >
                        Scheduler Logs (In-Time) ({scheduleLogs.length})
                      </button>
                      <button
                        onClick={() => setLogsSubTab("checkout-runs")}
                        className={`py-2 text-xs font-bold border-b-2 transition-all cursor-pointer bg-transparent border-none ${logsSubTab === "checkout-runs"
                          ? "border-[#133a69] text-[#133a69]"
                          : "border-transparent text-slate-400 hover:text-slate-650"
                          }`}
                      >
                        Scheduler Logs (Out-Time) ({checkoutLogs.length})
                      </button>
                    </div>
                    {logsSubTab === "sent-messages" && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[9px] font-black text-[#133a69] uppercase tracking-wider">Select Date:</span>
                        <input
                          type="date"
                          value={testNotificationDate}
                          onChange={handleDateChange}
                          max={new Date().toISOString().split("T")[0]}
                          className="px-3 py-1 border border-slate-200 rounded-lg text-xs font-bold focus:outline-none focus:border-[#133a69] bg-white shadow-2xs text-slate-700 cursor-pointer"
                        />
                      </div>
                    )}
                  </div>

                  {/* Logs Content Body */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-custom max-h-[350px]">
                    {/* Render subtab: Sent logs */}
                    {logsSubTab === "sent-messages" && (
                      <>
                        {logsLoading ? (
                          <div className="flex items-center justify-center h-full py-12">
                            <Loader2 className="w-6 h-6 text-[#133a69] animate-spin" />
                          </div>
                        ) : attendanceLogs.length > 0 ? (
                          attendanceLogs.map((log) => (
                            <div key={log._id} className="flex items-center justify-between p-3.5 bg-slate-50/30 hover:bg-slate-50 border border-slate-200/55 rounded-2xl transition-all hover:shadow-xs animate-[fadeIn_0.15s_ease-out]">
                              <div className="flex items-center gap-3 min-w-0 flex-1 mr-3">
                                <img
                                  src={log.recipientPhoto || defaultProfile}
                                  alt={log.recipientName}
                                  className="w-9 h-9 rounded-xl border object-cover shadow-xs shrink-0"
                                  onError={(e) => {
                                    e.target.src = defaultProfile;
                                  }}
                                />
                                <div className="min-w-0 flex-1">
                                  <span className="text-xs font-extrabold text-slate-800 block truncate">
                                    {log.recipientName}
                                  </span>
                                  <span className="text-[10px] text-slate-500 font-semibold block mt-0.5 truncate" title={log.message}>
                                    {log.message.replace(/\n/g, " ")}
                                  </span>
                                </div>
                              </div>
                              <span className="text-[9px] bg-blue-50 text-[#133a69] border border-blue-100 px-2 py-1 rounded-lg font-bold shrink-0">
                                {new Date(log.sentTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full py-16 text-slate-400">
                            <div className="bg-slate-100 rounded-3xl w-14 h-14 flex items-center justify-center mb-3 border border-slate-200/80 shadow-xs text-slate-400">
                              <Send className="w-6 h-6" />
                            </div>
                            <p className="text-xs font-bold text-slate-550">No notifications sent on this date</p>
                            <p className="text-[10px] text-slate-400 mt-1 max-w-[220px] text-center font-medium">Select a date and click trigger to dispatch notifications.</p>
                          </div>
                        )}
                      </>
                    )}

                    {/* Render subtab: Scheduler execution logs */}
                    {logsSubTab === "schedule-runs" && (
                      <>
                        {scheduleLogsLoading ? (
                          <div className="flex items-center justify-center h-full py-12">
                            <Loader2 className="w-6 h-6 text-[#133a69] animate-spin" />
                          </div>
                        ) : scheduleLogs.length > 0 ? (
                          scheduleLogs.map((runLog) => {
                            const isExpanded = expandedLogId === runLog._id;

                            // Status specific configuration
                            let statusConfig = {
                              badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200",
                              dotBg: "bg-emerald-500",
                              label: "Success",
                              icon: <Check className="w-3 h-3 text-emerald-600" />
                            };

                            if (runLog.status === "partial") {
                              statusConfig = {
                                badgeBg: "bg-amber-50 text-amber-700 border-amber-250",
                                dotBg: "bg-amber-500",
                                label: "Partial Success",
                                icon: <Loader2 className="w-3 h-3 text-amber-600 animate-spin" />
                              };
                            } else if (runLog.status === "failed") {
                              statusConfig = {
                                badgeBg: "bg-rose-50 text-rose-700 border-rose-200",
                                dotBg: "bg-rose-500",
                                label: "Failed",
                                icon: <Shield size={12} className="text-rose-600" />
                              };
                            }

                            return (
                              <div
                                key={runLog._id}
                                className="bg-slate-50/40 hover:bg-slate-50/70 border border-slate-200/60 rounded-2xl p-4 transition-all space-y-3.5 shadow-sm animate-[fadeIn_0.15s_ease-out] hover:shadow-md"
                              >
                                {/* Header Row */}
                                <div className="flex justify-between items-center gap-3">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <div className={`flex items-center justify-center w-6 h-6 rounded-lg ${statusConfig.badgeBg.split(" ")[0]} border`}>
                                      {statusConfig.icon}
                                    </div>
                                    <div className="min-w-0">
                                      <span className="text-xs font-black text-slate-800 block">
                                        Run at {new Date(runLog.executionTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                      </span>
                                      <span className="text-[10px] text-slate-500 font-bold block mt-0.5">
                                        {new Date(runLog.executionTime).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-[9px] bg-slate-100 border border-slate-200 text-slate-650 px-2.5 py-1 rounded-lg font-extrabold">
                                      Config Time: {runLog.scheduledTime}
                                    </span>
                                    <span className={`text-[9px] border px-2.5 py-1 rounded-lg font-black flex items-center gap-1 ${statusConfig.badgeBg}`}>
                                      <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dotBg}`} />
                                      {statusConfig.label}
                                    </span>
                                  </div>
                                </div>

                                {/* Stats row */}
                                <div className="grid grid-cols-3 gap-3">
                                  <div className="bg-white border border-slate-100 rounded-xl p-2.5 text-center shadow-2xs hover:shadow-xs transition-all border-l-3 border-l-slate-400">
                                    <span className="text-[9px] font-bold text-slate-450 block uppercase tracking-wider text-left pl-1">Processed</span>
                                    <span className="text-sm font-black text-slate-800 mt-1 block text-left pl-1">{runLog.processedCount}</span>
                                  </div>
                                  <div className="bg-white border border-slate-100 rounded-xl p-2.5 text-center shadow-2xs hover:shadow-xs transition-all border-l-3 border-l-emerald-500">
                                    <span className="text-[9px] font-bold text-slate-450 block uppercase tracking-wider text-left pl-1">Success</span>
                                    <span className="text-sm font-black text-emerald-600 mt-1 block text-left pl-1">{runLog.successCount}</span>
                                  </div>
                                  <div className="bg-white border border-slate-100 rounded-xl p-2.5 text-center shadow-2xs hover:shadow-xs transition-all border-l-3 border-l-rose-500">
                                    <span className="text-[9px] font-bold text-slate-450 block uppercase tracking-wider text-left pl-1">Failed</span>
                                    <span className="text-sm font-black text-rose-600 mt-1 block text-left pl-1">{runLog.failedCount}</span>
                                  </div>
                                </div>

                                {/* Toggle details button */}
                                <div className="pt-1.5 border-t border-slate-200/50 flex justify-between items-center">
                                  <span className="text-[10px] text-slate-450 font-bold">
                                    {runLog.details?.length || 0} employees processed
                                  </span>
                                  <button
                                    onClick={() => setExpandedLogId(isExpanded ? null : runLog._id)}
                                    className="bg-transparent border-none text-[#133a69] hover:text-[#0f2d52] font-black text-[10px] flex items-center gap-1 cursor-pointer transition-all hover:translate-x-0.5"
                                  >
                                    {isExpanded ? "Hide Execution Details" : "View Execution Details"}
                                    <span className="text-xs">{isExpanded ? "▲" : "▼"}</span>
                                  </button>
                                </div>

                                {/* Expanded employee status list details */}
                                {isExpanded && runLog.details && runLog.details.length > 0 && (
                                  <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden shadow-2xs max-h-[220px] flex flex-col animate-[fadeIn_0.15s_ease-out]">
                                    <div className="bg-slate-50 border-b border-slate-150 px-3.5 py-2 flex justify-between items-center text-[10px] font-extrabold text-slate-500 uppercase tracking-wider shrink-0">
                                      <span>Employee</span>
                                      <span>Dispatch Status</span>
                                    </div>
                                    <div className="flex-1 overflow-y-auto divide-y divide-slate-100 p-1.5 space-y-1 scrollbar-custom text-[10px] font-bold">
                                      {runLog.details.map((d, dIdx) => {
                                        let detailStatusClass = "bg-slate-50 text-slate-500 border-slate-150";
                                        let detailStatusText = "Skipped";

                                        if (d.status === "success") {
                                          detailStatusClass = "bg-emerald-50 text-emerald-700 border-emerald-150";
                                          detailStatusText = "Success";
                                        } else if (d.status === "failed") {
                                          detailStatusClass = "bg-rose-50 text-rose-700 border-rose-150";
                                          detailStatusText = "Failed";
                                        }

                                        return (
                                          <div key={dIdx} className="flex justify-between items-start py-2 px-2 hover:bg-slate-50/50 rounded-lg transition-all">
                                            <div className="min-w-0 flex-1 mr-2 text-left">
                                              <span className="text-slate-800 font-extrabold block truncate">{d.employeeName}</span>
                                              {d.error && (
                                                <span className="text-[9px] text-slate-450 font-medium block mt-0.5 break-words leading-relaxed">{d.error}</span>
                                              )}
                                            </div>
                                            <span className={`text-[8px] border px-2 py-0.5 rounded-md font-extrabold shrink-0 uppercase tracking-wider ${detailStatusClass}`}>
                                              {detailStatusText}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full py-16 text-slate-400">
                            <div className="bg-slate-100 rounded-3xl w-14 h-14 flex items-center justify-center mb-3 border border-slate-200/80 shadow-xs text-slate-400">
                              <Shield className="w-6 h-6" />
                            </div>
                            <p className="text-xs font-bold text-slate-550">No auto-schedule runs recorded yet</p>
                            <p className="text-[10px] text-slate-400 mt-1 max-w-[220px] text-center font-medium">Logs will appear here once the scheduler executes at its configured daily time.</p>
                          </div>
                        )}
                      </>
                    )}

                    {/* Render subtab: Checkout scheduler execution logs */}
                    {logsSubTab === "checkout-runs" && (
                      <>
                        {checkoutLogsLoading ? (
                          <div className="flex items-center justify-center h-full py-12">
                            <Loader2 className="w-6 h-6 text-[#133a69] animate-spin" />
                          </div>
                        ) : checkoutLogs.length > 0 ? (
                          checkoutLogs.map((runLog) => {
                            const isExpanded = expandedLogId === runLog._id;

                            // Status specific configuration
                            let statusConfig = {
                              badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200",
                              dotBg: "bg-emerald-500",
                              label: "Success",
                              icon: <Check className="w-3 h-3 text-emerald-600" />
                            };

                            if (runLog.status === "partial") {
                              statusConfig = {
                                badgeBg: "bg-amber-50 text-amber-700 border-amber-250",
                                dotBg: "bg-amber-500",
                                label: "Partial Success",
                                icon: <Loader2 className="w-3 h-3 text-amber-600 animate-spin" />
                              };
                            } else if (runLog.status === "failed") {
                              statusConfig = {
                                badgeBg: "bg-rose-50 text-rose-700 border-rose-200",
                                dotBg: "bg-rose-500",
                                label: "Failed",
                                icon: <Shield size={12} className="text-rose-600" />
                              };
                            }

                            return (
                              <div
                                key={runLog._id}
                                className="bg-slate-50/40 hover:bg-slate-50/70 border border-slate-200/60 rounded-2xl p-4 transition-all space-y-3.5 shadow-sm animate-[fadeIn_0.15s_ease-out] hover:shadow-md"
                              >
                                {/* Header Row */}
                                <div className="flex justify-between items-center gap-3">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <div className={`flex items-center justify-center w-6 h-6 rounded-lg ${statusConfig.badgeBg.split(" ")[0]} border`}>
                                      {statusConfig.icon}
                                    </div>
                                    <div className="min-w-0">
                                      <span className="text-xs font-black text-slate-800 block">
                                        Run at {new Date(runLog.executionTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                      </span>
                                      <span className="text-[10px] text-slate-500 font-bold block mt-0.5">
                                        {new Date(runLog.executionTime).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-[9px] bg-slate-100 border border-slate-200 text-slate-650 px-2.5 py-1 rounded-lg font-extrabold">
                                      Config Time: {runLog.scheduledTime}
                                    </span>
                                    <span className={`text-[9px] border px-2.5 py-1 rounded-lg font-black flex items-center gap-1 ${statusConfig.badgeBg}`}>
                                      <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dotBg}`} />
                                      {statusConfig.label}
                                    </span>
                                  </div>
                                </div>

                                {/* Stats row */}
                                <div className="grid grid-cols-3 gap-3">
                                  <div className="bg-white border border-slate-100 rounded-xl p-2.5 text-center shadow-2xs hover:shadow-xs transition-all border-l-3 border-l-slate-400">
                                    <span className="text-[9px] font-bold text-slate-450 block uppercase tracking-wider text-left pl-1">Processed</span>
                                    <span className="text-sm font-black text-slate-800 mt-1 block text-left pl-1">{runLog.processedCount}</span>
                                  </div>
                                  <div className="bg-white border border-slate-100 rounded-xl p-2.5 text-center shadow-2xs hover:shadow-xs transition-all border-l-3 border-l-emerald-500">
                                    <span className="text-[9px] font-bold text-slate-450 block uppercase tracking-wider text-left pl-1">Success</span>
                                    <span className="text-sm font-black text-emerald-600 mt-1 block text-left pl-1">{runLog.successCount}</span>
                                  </div>
                                  <div className="bg-white border border-slate-100 rounded-xl p-2.5 text-center shadow-2xs hover:shadow-xs transition-all border-l-3 border-l-rose-500">
                                    <span className="text-[9px] font-bold text-slate-450 block uppercase tracking-wider text-left pl-1">Failed</span>
                                    <span className="text-sm font-black text-rose-600 mt-1 block text-left pl-1">{runLog.failedCount}</span>
                                  </div>
                                </div>

                                {/* Toggle details button */}
                                <div className="pt-1.5 border-t border-slate-200/50 flex justify-between items-center">
                                  <span className="text-[10px] text-slate-450 font-bold">
                                    {runLog.details?.length || 0} employees processed
                                  </span>
                                  <button
                                    onClick={() => setExpandedLogId(isExpanded ? null : runLog._id)}
                                    className="bg-transparent border-none text-[#133a69] hover:text-[#0f2d52] font-black text-[10px] flex items-center gap-1 cursor-pointer transition-all hover:translate-x-0.5"
                                  >
                                    {isExpanded ? "Hide Execution Details" : "View Execution Details"}
                                    <span className="text-xs">{isExpanded ? "▲" : "▼"}</span>
                                  </button>
                                </div>

                                {/* Expanded employee status list details */}
                                {isExpanded && runLog.details && runLog.details.length > 0 && (
                                  <div className="bg-white border border-slate-200/80 rounded-xl overflow-hidden shadow-2xs max-h-[220px] flex flex-col animate-[fadeIn_0.15s_ease-out]">
                                    <div className="bg-slate-50 border-b border-slate-150 px-3.5 py-2 flex justify-between items-center text-[10px] font-extrabold text-slate-500 uppercase tracking-wider shrink-0">
                                      <span>Employee</span>
                                      <span>Dispatch Status</span>
                                    </div>
                                    <div className="flex-1 overflow-y-auto divide-y divide-slate-100 p-1.5 space-y-1 scrollbar-custom text-[10px] font-bold">
                                      {runLog.details.map((d, dIdx) => {
                                        let detailStatusClass = "bg-slate-50 text-slate-500 border-slate-150";
                                        let detailStatusText = "Skipped";

                                        if (d.status === "success") {
                                          detailStatusClass = "bg-emerald-50 text-emerald-700 border-emerald-150";
                                          detailStatusText = "Success";
                                        } else if (d.status === "failed") {
                                          detailStatusClass = "bg-rose-50 text-rose-700 border-rose-150";
                                          detailStatusText = "Failed";
                                        }

                                        return (
                                          <div key={dIdx} className="flex justify-between items-start py-2 px-2 hover:bg-slate-50/50 rounded-lg transition-all">
                                            <div className="min-w-0 flex-1 mr-2 text-left">
                                              <span className="text-slate-800 font-extrabold block truncate">{d.employeeName}</span>
                                              {d.error && (
                                                <span className="text-[9px] text-slate-450 font-medium block mt-0.5 break-words leading-relaxed">{d.error}</span>
                                              )}
                                            </div>
                                            <span className={`text-[8px] border px-2 py-0.5 rounded-md font-extrabold shrink-0 uppercase tracking-wider ${detailStatusClass}`}>
                                              {detailStatusText}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full py-16 text-slate-400">
                            <div className="bg-slate-100 rounded-3xl w-14 h-14 flex items-center justify-center mb-3 border border-slate-200/80 shadow-xs text-slate-400">
                              <Shield className="w-6 h-6" />
                            </div>
                            <p className="text-xs font-bold text-slate-550">No checkout runs recorded yet</p>
                            <p className="text-[10px] text-slate-400 mt-1 max-w-[220px] text-center font-medium">Logs will appear here once the out-time scheduler executes at its configured daily time.</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "holidays" && (
              <div className="flex flex-col lg:flex-row gap-6 h-full animate-[fadeIn_0.2s_ease-out]">
                {/* Left Side: Add Holiday Form */}
                <div className="w-full lg:w-1/3 bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs shrink-0 self-start">
                  <h3 className="text-sm font-black text-slate-800 mb-1">Add Company Holiday</h3>
                  <p className="text-[11px] text-slate-400 font-bold block mb-4">
                    Configure official non-working days. Absence warnings will be auto-suppressed and holiday greetings will be broadcasted.
                  </p>

                  <form onSubmit={handleAddHoliday} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1.5 ml-0.5">
                          Holiday Date
                        </label>
                        <input
                          type="date"
                          required
                          value={holidayDate}
                          onChange={(e) => setHolidayDate(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:border-[#133a69] bg-white text-slate-700 cursor-pointer"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1.5 ml-0.5">
                          Sending Time
                        </label>
                        <input
                          type="time"
                          required
                          value={holidaySendTime}
                          onChange={(e) => setHolidaySendTime(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:border-[#133a69] bg-white text-slate-700 cursor-pointer"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1.5 ml-0.5">
                        Holiday Name
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Independence Day, Diwali"
                        value={holidayName}
                        onChange={(e) => setHolidayName(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:border-[#133a69] bg-white text-slate-700"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1.5 ml-0.5">
                        Description (Optional)
                      </label>
                      <textarea
                        placeholder="Provide details about this holiday..."
                        value={holidayDescription}
                        onChange={(e) => setHolidayDescription(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:border-[#133a69] bg-white text-slate-700 resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1.5 ml-0.5">
                        Holiday Post Banner / Image (Optional)
                      </label>
                      <div className="flex items-center gap-3 mt-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleHolidayImageChange}
                          id="holiday-image-upload"
                          className="hidden"
                        />
                        <label
                          htmlFor="holiday-image-upload"
                          className="px-4 py-2 border border-dashed border-slate-300 rounded-xl text-xs font-bold text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors flex items-center gap-1.5"
                        >
                          <span>📁</span> {holidayImageFile ? holidayImageFile.name : "Select Image"}
                        </label>
                        {holidayImagePreview && (
                          <div className="relative w-24 h-16 rounded-xl overflow-hidden border border-slate-200 bg-slate-900/5 p-1 flex items-center justify-center">
                            <img src={holidayImagePreview} alt="Preview" className="max-w-full max-h-full object-contain" />
                            <button
                              type="button"
                              onClick={clearHolidayImage}
                              className="absolute top-0.5 right-0.5 bg-rose-500 hover:bg-rose-600 text-white rounded-full p-0.5 flex items-center justify-center cursor-pointer border-none shadow-xs"
                              style={{ width: "18px", height: "18px" }}
                            >
                              <span style={{ fontSize: "10px", fontWeight: "bold" }}>×</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSavingHoliday}
                      className="w-full py-2.5 bg-[#133a69] hover:bg-[#0f2d52] text-white rounded-xl text-xs font-bold shadow-sm transition-all hover:scale-[1.01] cursor-pointer border-none flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingHoliday ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Plus size={14} />
                      )}
                      Add Holiday
                    </button>
                  </form>
                </div>

                {/* Right Side: Holidays List */}
                <div className="flex-1 bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-xs flex flex-col min-h-[460px]">
                  <div className="bg-slate-50 border-b border-slate-200 px-5 py-3.5 shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-black text-slate-800">Configured Company Holidays</h3>
                        <span className="text-[11px] bg-[#133a69]/10 text-[#133a69] border border-[#133a69]/20 px-2 py-0.5 rounded-full font-bold">
                          {holidaySearchQuery.trim() ? `${filteredHolidays.length} / ${holidays.length}` : holidays.length}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 font-medium block mt-0.5">
                        List of holidays registered for the current calendar year.
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Search Input */}
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                          type="text"
                          placeholder="Search holiday..."
                          value={holidaySearchQuery}
                          onChange={(e) => setHolidaySearchQuery(e.target.value)}
                          className="pl-8 pr-6 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-[#133a69] w-36 sm:w-44 transition-all shadow-2xs"
                        />
                        {holidaySearchQuery && (
                          <button
                            type="button"
                            onClick={() => setHolidaySearchQuery("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-650 text-xs border-none bg-transparent cursor-pointer font-bold"
                          >
                            ×
                          </button>
                        )}
                      </div>

                      {/* View Mode Toggle: List vs Grid */}
                      <div className="flex items-center bg-slate-200/60 p-0.5 rounded-xl border border-slate-200/80">
                        <button
                          type="button"
                          onClick={() => setHolidayViewMode("list")}
                          className={`px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer border-none ${
                            holidayViewMode === "list"
                              ? "bg-white text-[#133a69] shadow-xs"
                              : "bg-transparent text-slate-500 hover:text-slate-800"
                          }`}
                          title="List View (Full Details & Proper View)"
                        >
                          <LayoutList size={13} />
                          <span className="hidden sm:inline">List</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setHolidayViewMode("grid")}
                          className={`px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer border-none ${
                            holidayViewMode === "grid"
                              ? "bg-white text-[#133a69] shadow-xs"
                              : "bg-transparent text-slate-500 hover:text-slate-800"
                          }`}
                          title="Grid View (Cards)"
                        >
                          <LayoutGrid size={13} />
                          <span className="hidden sm:inline">Grid</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3.5 scrollbar-custom max-h-[calc(100vh-270px)] min-h-[460px]">
                    {holidaysLoading ? (
                      <div className="flex items-center justify-center h-full py-16">
                        <Loader2 className="w-6 h-6 text-[#133a69] animate-spin" />
                      </div>
                    ) : filteredHolidays.length > 0 ? (
                      holidayViewMode === "list" ? (
                        /* === LIST VIEW: Detailed & Uncropped === */
                        <div className="space-y-3.5">
                          {filteredHolidays.map((holiday) => {
                            const dateInfo = formatHolidayDate(holiday.date);
                            return (
                              <div
                                key={holiday._id}
                                className="p-4 sm:p-5 bg-white hover:bg-slate-50/60 border border-slate-200/90 rounded-2xl transition-all hover:shadow-md hover:border-[#133a69]/25 flex flex-col md:flex-row items-stretch gap-4 sm:gap-5 group"
                              >
                                {/* Left Section: Information */}
                                <div className="flex-1 flex flex-col justify-between min-w-0">
                                  <div>
                                    {/* Top Meta Badges & Action Buttons */}
                                    <div className="flex items-center justify-between gap-2 flex-wrap mb-2.5">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        {/* Date Badge with Day of Week */}
                                        <span className="text-xs bg-[#133a69]/10 text-[#133a69] border border-[#133a69]/20 px-3 py-1 rounded-xl font-black flex items-center gap-1.5 shadow-2xs">
                                          <Calendar size={13} className="text-[#133a69]" />
                                          {dateInfo.formatted}
                                        </span>

                                        {/* Sending Time Badge */}
                                        <span className="text-xs bg-slate-100 text-slate-700 border border-slate-200 px-2.5 py-1 rounded-xl font-bold flex items-center gap-1.5 shadow-2xs">
                                          <Clock size={12} className="text-slate-500" />
                                          {formatSendTime12h(holiday.sendTime)}
                                        </span>

                                        {/* Broadcast Status Badge */}
                                        {holiday.isSent ? (
                                          <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-xl font-bold flex items-center gap-1 shadow-2xs">
                                            <CheckCheck size={13} /> Sent
                                          </span>
                                        ) : (
                                          <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 rounded-xl font-bold flex items-center gap-1 shadow-2xs">
                                            <Clock size={12} /> Scheduled
                                          </span>
                                        )}
                                      </div>

                                      {/* Actions */}
                                      <div className="flex items-center gap-1.5 shrink-0">
                                        {holiday.image && (
                                          <button
                                            type="button"
                                            onClick={() => setLightboxImage(holiday.image)}
                                            className="p-1.5 sm:p-2 rounded-xl bg-white border border-slate-200 text-slate-600 hover:text-[#133a69] hover:bg-blue-50 hover:border-blue-200 transition-all cursor-pointer shadow-2xs"
                                            title="View Banner Fullscreen"
                                          >
                                            <Maximize2 size={13} />
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          onClick={() => handleOpenEditHolidayModal(holiday)}
                                          className="p-1.5 sm:p-2 rounded-xl bg-white border border-slate-200 text-slate-600 hover:text-[#133a69] hover:bg-blue-50 hover:border-blue-200 transition-all cursor-pointer shadow-2xs"
                                          title="Edit Holiday"
                                        >
                                          <Edit size={13} />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteHoliday(holiday._id)}
                                          className="p-1.5 sm:p-2 rounded-xl bg-white border border-slate-200 text-slate-600 hover:text-rose-600 hover:bg-rose-50 hover:border-rose-200 transition-all cursor-pointer shadow-2xs"
                                          title="Delete Holiday"
                                        >
                                          <Trash2 size={13} />
                                        </button>
                                      </div>
                                    </div>

                                    {/* Holiday Name */}
                                    <h4 className="text-base font-black text-slate-800 mb-2 tracking-tight">
                                      {holiday.name}
                                    </h4>

                                    {/* Full Description without truncation */}
                                    {holiday.description ? (
                                      <p className="text-xs text-slate-600 leading-relaxed font-medium whitespace-pre-line bg-slate-50/80 rounded-xl p-3 border border-slate-200/70">
                                        {holiday.description}
                                      </p>
                                    ) : (
                                      <p className="text-xs text-slate-400 italic">
                                        No description provided.
                                      </p>
                                    )}
                                  </div>
                                </div>

                                {/* Right Section: Complete Banner Image (No crop) */}
                                {holiday.image && (
                                  <div
                                    onClick={() => setLightboxImage(holiday.image)}
                                    className="w-full md:w-64 lg:w-72 xl:w-80 h-40 sm:h-44 rounded-xl bg-slate-900/5 border border-slate-200 overflow-hidden flex items-center justify-center p-2 cursor-pointer group/img relative shrink-0 transition-all hover:border-[#133a69]/40 hover:shadow-sm"
                                    title="Click to view full banner"
                                  >
                                    <img
                                      src={holiday.image}
                                      alt={holiday.name}
                                      className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover/img:scale-[1.02]"
                                    />
                                    <div className="absolute inset-0 bg-slate-900/35 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-1.5 text-white text-xs font-bold backdrop-blur-[1px]">
                                      <Eye size={14} /> Full Image
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        /* === GRID VIEW: Cards with Uncropped Preview === */
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {filteredHolidays.map((holiday) => {
                            const dateInfo = formatHolidayDate(holiday.date);
                            return (
                              <div
                                key={holiday._id}
                                className="flex flex-col justify-between p-4 bg-white hover:bg-slate-50/60 border border-slate-200/90 rounded-2xl transition-all hover:shadow-md animate-[fadeIn_0.15s_ease-out]"
                              >
                                <div>
                                  <div className="flex items-center justify-between gap-2 mb-2.5">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="text-[11px] bg-[#133a69]/10 text-[#133a69] border border-[#133a69]/20 px-2.5 py-0.5 rounded-lg font-black">
                                        {dateInfo.formatted}
                                      </span>
                                      <span className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 rounded-lg font-bold flex items-center gap-1">
                                        <Clock size={10} /> {formatSendTime12h(holiday.sendTime)}
                                      </span>
                                      {holiday.isSent ? (
                                        <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-lg font-bold">
                                          ✓ Sent
                                        </span>
                                      ) : (
                                        <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-lg font-bold">
                                          ⏳ Scheduled
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => handleOpenEditHolidayModal(holiday)}
                                        className="p-1.5 rounded-lg bg-white border border-slate-200 text-slate-500 hover:text-[#133a69] hover:bg-blue-50 transition-all cursor-pointer shadow-2xs"
                                        title="Edit Holiday"
                                      >
                                        <Edit size={12} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteHoliday(holiday._id)}
                                        className="p-1.5 rounded-lg bg-white border border-slate-200 text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-all cursor-pointer shadow-2xs"
                                        title="Delete Holiday"
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  </div>

                                  <h4 className="text-sm font-black text-slate-800 mb-1.5">
                                    {holiday.name}
                                  </h4>

                                  {holiday.description ? (
                                    <p className="text-xs text-slate-600 leading-relaxed font-medium whitespace-pre-line mb-3 bg-slate-50/80 rounded-xl p-2.5 border border-slate-200/70">
                                      {holiday.description}
                                    </p>
                                  ) : (
                                    <p className="text-xs text-slate-400 italic mb-2.5">
                                      No description provided.
                                    </p>
                                  )}

                                  {holiday.image && (
                                    <div
                                      onClick={() => setLightboxImage(holiday.image)}
                                      className="mt-2 rounded-xl overflow-hidden border border-slate-200 h-44 bg-slate-900/5 flex items-center justify-center p-2 cursor-pointer group/img relative"
                                      title="Click to view full image"
                                    >
                                      <img
                                        src={holiday.image}
                                        alt={holiday.name}
                                        className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover/img:scale-[1.02]"
                                      />
                                      <div className="absolute inset-0 bg-slate-900/35 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-1 text-white text-xs font-bold backdrop-blur-[1px]">
                                        <Eye size={13} /> Full Image
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )
                    ) : holidaySearchQuery.trim() ? (
                      <div className="flex flex-col items-center justify-center h-full py-16 text-slate-400">
                        <div className="bg-slate-100 rounded-2xl w-12 h-12 flex items-center justify-center mb-2.5 text-slate-400">
                          <Search className="w-5 h-5" />
                        </div>
                        <p className="text-xs font-bold text-slate-600">No holidays match "{holidaySearchQuery}"</p>
                        <button
                          type="button"
                          onClick={() => setHolidaySearchQuery("")}
                          className="mt-2 text-xs text-[#133a69] hover:underline font-bold bg-transparent border-none cursor-pointer"
                        >
                          Clear Search
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full py-20 text-slate-400">
                        <div className="bg-slate-100 rounded-3xl w-14 h-14 flex items-center justify-center mb-3 border border-slate-200/80 shadow-xs text-slate-400">
                          <Calendar className="w-6 h-6" />
                        </div>
                        <p className="text-xs font-bold text-slate-555">No company holidays configured</p>
                        <p className="text-[10px] text-slate-400 mt-1 max-w-[220px] text-center font-medium">
                          Use the form on the left to add your first company holiday.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Group Members Modal */}
      {isEditModalOpen && editingGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
          <div className="bg-white w-full max-w-md rounded-2xl border border-slate-200 shadow-xl overflow-hidden animate__animated animate__fadeInUp animate__faster flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between shrink-0">
              <div className="min-w-0">
                <h3 className="text-sm font-black text-slate-800 truncate">
                  {editingGroup.isDefaultSender ? "Edit System Channel Name" : "Manage Group Settings"}
                </h3>
                <p className="text-[11px] text-slate-400 font-bold block truncate mt-0.5">
                  {editingGroup.isDefaultSender ? `Channel: ${editingGroup.name}` : `Group: ${editingGroup.name}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="text-slate-400 hover:text-slate-650 p-1 border-none bg-transparent cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 p-5 overflow-y-auto space-y-4">
              {/* Group/Channel Name Input */}
              <div>
                <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1.5 ml-0.5">
                  {editingGroup.isDefaultSender ? "System Channel Name" : "Group Name"}
                </label>
                <input
                  type="text"
                  value={editGroupName}
                  onChange={(e) => setEditGroupName(e.target.value)}
                  required
                  placeholder={editingGroup.isDefaultSender ? "e.g. Dynamic Alerts" : "e.g. Inhouse RND Team"}
                  className="w-full px-3.5 py-2.5 border border-slate-200 focus:border-[#133a69] focus:ring-2 focus:ring-blue-500/10 rounded-xl text-xs bg-slate-50/30 transition-all font-medium placeholder:text-slate-400"
                />
              </div>

              {!editingGroup.isDefaultSender && (
                <>
                  {/* Search Bar */}
                  <div className="relative shrink-0 pt-2 border-t border-slate-100">
                    <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-2 ml-0.5">
                      Manage Members
                    </label>
                    <div className="relative">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
                      <input
                        type="text"
                        placeholder="Search members to add or remove..."
                        value={editSearchQuery}
                        onChange={(e) => setEditSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-[#133a69] focus:ring-2 focus:ring-blue-500/10 bg-[#f8fafc] transition-all"
                      />
                    </div>
                  </div>

                  {/* Members List checkboxes */}
                  <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                    {members
                      .filter((m) => m._id !== currentUserId)
                      .filter((m) => m.Name.toLowerCase().includes(editSearchQuery.toLowerCase()))
                      .map((m) => {
                        const isChecked = editingMemberIds.includes(m._id);
                        return (
                          <label
                            key={m._id}
                            className={`flex items-center justify-between p-2.5 border rounded-xl cursor-pointer transition-colors ${isChecked ? "bg-blue-50/30 border-blue-200" : "bg-white hover:bg-slate-50 border-slate-200"}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <img
                                src={m.ProfilePhoto || defaultProfile}
                                alt="Avatar"
                                className="w-8 h-8 rounded-lg object-cover border"
                                onError={(e) => {
                                  e.target.src = defaultProfile;
                                }}
                              />
                              <div className="min-w-0">
                                <span className="text-xs font-bold text-slate-800 block truncate">
                                  {m.Name}
                                </span>
                                <span className="text-[10px] text-slate-450 font-semibold block truncate">
                                  {m.Role.split(" ")[0]}
                                </span>
                              </div>
                            </div>

                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleEditCheckbox(m._id)}
                              className="rounded border-slate-350 text-[#133a69] focus:ring-[#133a69] w-4 h-4 shrink-0 ml-3"
                            />
                          </label>
                        );
                      })}
                  </div>

                  {editingMemberIds.length < 2 && (
                    <p className="text-[10px] text-rose-500 font-bold mt-1.5 flex items-center gap-1">
                      ⚠️ Please select at least 2 members for this group channel.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Modal Actions Footer */}
            <div className="bg-slate-50 border-t border-slate-200 px-5 py-4 flex gap-3 justify-end shrink-0">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2.5 border border-slate-200 hover:bg-slate-100 text-slate-650 rounded-xl text-xs font-bold transition-all cursor-pointer bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpdateMembers}
                disabled={isSavingMembers || !editGroupName.trim() || (!editingGroup.isDefaultSender && editingMemberIds.length < 2)}
                className="px-4 py-2.5 bg-[#133a69] hover:bg-[#0f2d52] disabled:bg-slate-300 text-white rounded-xl text-xs font-bold transition-all cursor-pointer border-none shadow-sm flex items-center gap-1.5"
              >
                {isSavingMembers ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Online Now List Modal */}
      {isOnlineModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
          <div className="bg-white w-full max-w-md rounded-2xl border border-slate-200 shadow-xl overflow-hidden animate__animated animate__fadeInUp animate__faster flex flex-col max-h-[80vh]">
            {/* Modal Header */}
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between shrink-0">
              <div className="min-w-0">
                <h3 className="text-sm font-black text-slate-800 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  Online Members ({totalOnlineCount})
                </h3>
                <p className="text-[11px] text-slate-400 font-bold block truncate mt-0.5">
                  Currently active employees in the system
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsOnlineModalOpen(false)}
                className="text-slate-400 hover:text-slate-650 p-1 border-none bg-transparent cursor-pointer text-base font-bold"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 p-4 overflow-y-auto space-y-3.5 scrollbar-custom min-h-[150px]">
              {members.filter(member => onlineUsers[member._id] && member._id !== currentUserId).length > 0 ? (
                members
                  .filter(member => onlineUsers[member._id] && member._id !== currentUserId)
                  .map((member) => (
                    <div
                      key={member._id}
                      className="flex items-center justify-between p-3.5 hover:bg-slate-50 rounded-2xl border border-slate-100 hover:border-slate-200/80 transition-all shadow-2xs hover:shadow-xs group"
                    >
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div className="relative shrink-0">
                          <img
                            src={member.ProfilePhoto || defaultProfile}
                            alt="Avatar"
                            className="w-11 h-11 rounded-xl border object-cover shadow-xs"
                            onError={(e) => {
                              e.target.src = defaultProfile;
                            }}
                          />
                          <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full animate-pulse"></span>
                        </div>
                        <div className="min-w-0 text-left">
                          <span className="text-xs font-black text-slate-800 block truncate group-hover:text-[#133a69] transition-all">
                            {member.Name}
                          </span>
                          <span className="text-[10px] text-slate-455 font-semibold block mt-0.5 truncate">
                            {member.Role} • {member.Email}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setIsOnlineModalOpen(false);
                          handleOpenMemberChat(member);
                        }}
                        className="px-3.5 py-2 bg-blue-50 hover:bg-[#133a69] text-[#133a69] hover:text-white rounded-xl text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1.5 shadow-2xs border border-transparent hover:border-[#133a69] shrink-0"
                      >
                        <MessageSquare size={13} /> Chat
                      </button>
                    </div>
                  ))
              ) : (
                <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                  <div className="bg-slate-100 rounded-3xl w-14 h-14 flex items-center justify-center mb-3 border border-slate-200/80 shadow-xs text-slate-400">
                    <Users className="w-6 h-6" />
                  </div>
                  <p className="text-xs font-bold text-slate-550">No online members</p>
                  <p className="text-[10px] text-slate-400 mt-1 max-w-[220px] text-center font-medium">All other coworkers are currently offline.</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 border-t border-slate-200 px-5 py-4 flex justify-end shrink-0">
              <button
                type="button"
                onClick={() => setIsOnlineModalOpen(false)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-extrabold transition-all cursor-pointer border-none shadow-xs"
              >
                Close Window
              </button>
            </div>
          </div>
        </div>
      )}

      {/* In-Time Recipients Selection Modal */}
      {isInTimeModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-[9999] animate-[fadeIn_0.15s_ease-out]">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="px-5 py-4 border-b border-slate-150 flex justify-between items-center bg-slate-50/50">
              <div className="text-left">
                <h3 className="text-sm font-black text-slate-800 tracking-wide m-0 flex items-center gap-1.5">
                  <Users size={15} className="text-[#133a69]" /> In-Time Recipients
                </h3>
                <p className="text-[10px] text-slate-400 font-medium m-0 mt-0.5">
                  Select coworkers who will receive check-in alerts
                </p>
              </div>
              <button
                onClick={() => setIsInTimeModalOpen(false)}
                className="text-slate-400 hover:text-slate-650 font-extrabold text-sm bg-transparent border-none cursor-pointer p-1"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 flex flex-col gap-3 overflow-hidden">
              {/* Search & Actions */}
              <div className="flex gap-2 shrink-0">
                <input
                  type="text"
                  placeholder="Search coworkers..."
                  value={inTimeSearch}
                  onChange={(e) => setInTimeSearch(e.target.value)}
                  className="flex-1 px-3 py-1.5 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-[#133a69] bg-slate-50/30"
                />
                <button
                  type="button"
                  onClick={() => setTempInTimeExcluded([])}
                  className="px-2.5 py-1.5 bg-blue-50 text-[#133a69] border border-blue-100 hover:bg-blue-100 rounded-xl text-[10px] font-black cursor-pointer"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => setTempInTimeExcluded(members.filter(m => m.Email !== "management@dynamic.com").map(m => m._id))}
                  className="px-2.5 py-1.5 bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 rounded-xl text-[10px] font-black cursor-pointer"
                >
                  Clear All
                </button>
              </div>

              {/* Scrollable checklist */}
              <div className="overflow-y-auto flex-1 pr-1 flex flex-col gap-1 min-h-[220px]">
                {members
                  .filter(m => m.Email !== "management@dynamic.com" && m.Name?.toLowerCase().includes(inTimeSearch.toLowerCase()))
                  .map(member => {
                    const isExcluded = tempInTimeExcluded.includes(member._id);
                    return (
                      <label key={member._id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 rounded-xl cursor-pointer transition-all border border-transparent hover:border-slate-100">
                        <div className="flex items-center gap-2.5">
                          <img
                            src={member.ProfilePhoto || defaultProfile}
                            alt="Avatar"
                            className="w-7 h-7 rounded-lg object-cover border border-slate-150"
                            onError={(e) => { e.target.src = defaultProfile; }}
                          />
                          <div className="flex flex-col text-left">
                            <span className="text-xs font-bold text-slate-800">{member.Name}</span>
                            <span className="text-[9px] text-slate-400 font-semibold">{member.Role}</span>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={!isExcluded}
                          onChange={() => {
                            let updated;
                            if (isExcluded) {
                              updated = tempInTimeExcluded.filter(id => id !== member._id);
                            } else {
                              updated = [...tempInTimeExcluded, member._id];
                            }
                            setTempInTimeExcluded(updated);
                          }}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                        />
                      </label>
                    );
                  })}
                {members.filter(m => m.Email !== "management@dynamic.com" && m.Name?.toLowerCase().includes(inTimeSearch.toLowerCase())).length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8">
                    <span className="text-xs font-bold text-slate-400 italic">No coworkers found</span>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-3.5 border-t border-slate-150 bg-slate-50/50 flex justify-end gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setIsInTimeModalOpen(false)}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-555 hover:text-slate-700 bg-white cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setInTimeExcludedEmployees(tempInTimeExcluded);
                  setIsInTimeModalOpen(false);
                }}
                className="px-4 py-2 bg-[#133a69] hover:bg-[#0f2d52] text-white rounded-xl text-xs font-bold shadow-sm cursor-pointer border-none"
              >
                Apply Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Out-Time Recipients Selection Modal */}
      {isOutTimeModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-[9999] animate-[fadeIn_0.15s_ease-out]">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="px-5 py-4 border-b border-slate-150 flex justify-between items-center bg-slate-50/50">
              <div className="text-left">
                <h3 className="text-sm font-black text-slate-800 tracking-wide m-0 flex items-center gap-1.5">
                  <Users size={15} className="text-[#133a69]" /> Out-Time Recipients
                </h3>
                <p className="text-[10px] text-slate-400 font-medium m-0 mt-0.5">
                  Select coworkers who will receive check-out alerts
                </p>
              </div>
              <button
                onClick={() => setIsOutTimeModalOpen(false)}
                className="text-slate-400 hover:text-slate-650 font-extrabold text-sm bg-transparent border-none cursor-pointer p-1"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 flex flex-col gap-3 overflow-hidden">
              {/* Search & Actions */}
              <div className="flex gap-2 shrink-0">
                <input
                  type="text"
                  placeholder="Search coworkers..."
                  value={outTimeSearch}
                  onChange={(e) => setOutTimeSearch(e.target.value)}
                  className="flex-1 px-3 py-1.5 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-[#133a69] bg-slate-50/30"
                />
                <button
                  type="button"
                  onClick={() => setTempOutTimeExcluded([])}
                  className="px-2.5 py-1.5 bg-blue-50 text-[#133a69] border border-blue-100 hover:bg-blue-100 rounded-xl text-[10px] font-black cursor-pointer"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => setTempOutTimeExcluded(members.filter(m => m.Email !== "management@dynamic.com").map(m => m._id))}
                  className="px-2.5 py-1.5 bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 rounded-xl text-[10px] font-black cursor-pointer"
                >
                  Clear All
                </button>
              </div>

              {/* Scrollable checklist */}
              <div className="overflow-y-auto flex-1 pr-1 flex flex-col gap-1 min-h-[220px]">
                {members
                  .filter(m => m.Email !== "management@dynamic.com" && m.Name?.toLowerCase().includes(outTimeSearch.toLowerCase()))
                  .map(member => {
                    const isExcluded = tempOutTimeExcluded.includes(member._id);
                    return (
                      <label key={member._id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 rounded-xl cursor-pointer transition-all border border-transparent hover:border-slate-100">
                        <div className="flex items-center gap-2.5">
                          <img
                            src={member.ProfilePhoto || defaultProfile}
                            alt="Avatar"
                            className="w-7 h-7 rounded-lg object-cover border border-slate-150"
                            onError={(e) => { e.target.src = defaultProfile; }}
                          />
                          <div className="flex flex-col text-left">
                            <span className="text-xs font-bold text-slate-800">{member.Name}</span>
                            <span className="text-[9px] text-slate-400 font-semibold">{member.Role}</span>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={!isExcluded}
                          onChange={() => {
                            let updated;
                            if (isExcluded) {
                              updated = tempOutTimeExcluded.filter(id => id !== member._id);
                            } else {
                              updated = [...tempOutTimeExcluded, member._id];
                            }
                            setTempOutTimeExcluded(updated);
                          }}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                        />
                      </label>
                    );
                  })}
                {members.filter(m => m.Email !== "management@dynamic.com" && m.Name?.toLowerCase().includes(outTimeSearch.toLowerCase())).length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8">
                    <span className="text-xs font-bold text-slate-400 italic">No coworkers found</span>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-3.5 border-t border-slate-150 bg-slate-50/50 flex justify-end gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setIsOutTimeModalOpen(false)}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-555 hover:text-slate-700 bg-white cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setOutTimeExcludedEmployees(tempOutTimeExcluded);
                  setIsOutTimeModalOpen(false);
                }}
                className="px-4 py-2 bg-[#133a69] hover:bg-[#0f2d52] text-white rounded-xl text-xs font-bold shadow-sm cursor-pointer border-none"
              >
                Apply Selection
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Holiday Modal */}
      {isEditHolidayModalOpen && editingHoliday && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-[9999] animate-[fadeIn_0.15s_ease-out]">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between shrink-0">
              <div>
                <h3 className="text-sm font-black text-slate-800">Edit Company Holiday</h3>
                <p className="text-[10px] text-slate-400 font-bold block mt-0.5">
                  Update date or holiday information.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsEditHolidayModalOpen(false);
                  setEditingHoliday(null);
                }}
                className="text-slate-400 hover:text-slate-650 p-1 border-none bg-transparent cursor-pointer font-bold"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleUpdateHoliday} className="p-5 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1.5 ml-0.5">
                    Holiday Date
                  </label>
                  <input
                    type="date"
                    required
                    value={editHolidayDate}
                    onChange={(e) => setEditHolidayDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:border-[#133a69] bg-white text-slate-700 cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1.5 ml-0.5">
                    Sending Time
                  </label>
                  <input
                    type="time"
                    required
                    value={editHolidaySendTime}
                    onChange={(e) => setEditHolidaySendTime(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:border-[#133a69] bg-white text-slate-700 cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1.5 ml-0.5">
                  Holiday Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Independence Day"
                  value={editHolidayName}
                  onChange={(e) => setEditHolidayName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:border-[#133a69] bg-white text-slate-700"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1.5 ml-0.5">
                  Description (Optional)
                </label>
                <textarea
                  placeholder="Provide details about this holiday..."
                  value={editHolidayDescription}
                  onChange={(e) => setEditHolidayDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:border-[#133a69] bg-white text-slate-700 resize-none"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider mb-1.5 ml-0.5">
                  Holiday Post Banner / Image (Optional)
                </label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleEditHolidayImageChange}
                    id="edit-holiday-image-upload"
                    className="hidden"
                  />
                  <label
                    htmlFor="edit-holiday-image-upload"
                    className="px-4 py-2 border border-dashed border-slate-300 rounded-xl text-xs font-bold text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors flex items-center gap-1.5"
                  >
                    <span>📁</span> {editHolidayImageFile ? editHolidayImageFile.name : (editHolidayImage ? "Change Image" : "Select Image")}
                  </label>
                  {editHolidayImagePreview && (
                    <div className="relative w-24 h-16 rounded-xl overflow-hidden border border-slate-200 bg-slate-900/5 p-1 flex items-center justify-center">
                      <img src={editHolidayImagePreview} alt="Preview" className="max-w-full max-h-full object-contain" />
                      <button
                        type="button"
                        onClick={clearEditHolidayImage}
                        className="absolute top-0.5 right-0.5 bg-rose-500 hover:bg-rose-600 text-white rounded-full p-0.5 flex items-center justify-center cursor-pointer border-none shadow-xs"
                        style={{ width: "18px", height: "18px" }}
                      >
                        <span style={{ fontSize: "10px", fontWeight: "bold" }}>×</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer Actions */}
              <div className="pt-4 border-t border-slate-150 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditHolidayModalOpen(false);
                    setEditingHoliday(null);
                  }}
                  className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-555 hover:text-slate-700 bg-white cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingHoliday}
                  className="px-4 py-2 bg-[#133a69] hover:bg-[#0f2d52] text-white rounded-xl text-xs font-bold shadow-sm cursor-pointer border-none flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  {isSavingHoliday && <Loader2 className="w-3 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Lightbox Modal */}
      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px"
          }}
        >
          <div style={{ position: "relative" }}>
            <img
              src={lightboxImage}
              alt="Enlarged Chat Attachment"
              style={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                borderRadius: "8px",
                objectFit: "contain",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)"
              }}
            />
            <button
              onClick={() => setLightboxImage(null)}
              style={{
                position: "absolute",
                top: "-45px",
                right: 0,
                backgroundColor: "rgba(255,255,255,0.15)",
                border: "none",
                color: "white",
                padding: "8px",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <X size={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadImage(lightboxImage);
              }}
              style={{
                position: "absolute",
                top: "-45px",
                right: "45px",
                backgroundColor: "rgba(255,255,255,0.15)",
                border: "none",
                color: "white",
                padding: "8px",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Download size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerChatManagement;
